// src/components/DrainageEditor.tsx
import { buildInterpolator, GLPoint, GLContour } from '../lib/tin';
import { exportPdf, Prim, PdfOpts } from '../lib/exportPdf';
import { exportExcel } from '../lib/exportExcel';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Stage, Layer, Group, Circle, RegularPolygon, Line, Text, Arrow, Image as KImage } from 'react-konva';
import {
  DrainNode, DrainLink, DesignSettings, Condition, OptParams,
  computeNetwork, RUNOFF_COEFF, availableReturnPeriods, MANNING_N, KS_VALUES,
  optimiseNetwork, withAllocatedCatchments, resolvedStormTable, DEFAULT_OPT_PARAMS,
} from '../lib/engine';
import * as pdfjsLib from 'pdfjs-dist';
// Vite: serves the worker from our own origin (CSP-safe)
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const uid=(p:string)=>p+Math.random().toString(36).slice(2,8);
const ORIGIN={e:832000,n:830000};
const worldToBase=(e:number,n:number)=>({x:e-ORIGIN.e,y:-(n-ORIGIN.n)});
const baseToWorld=(x:number,y:number)=>({e:x+ORIGIN.e,n:ORIGIN.n-y});
const SNAP=0.5; const snap=(v:number)=>Math.round(v/SNAP)*SNAP;
type Pt={e:number;n:number};
type Off={x:number;y:number};
type Tool='select'|'manhole'|'catchpit'|'outfall'|'connect'|'catchment'|'calibrate'|'ruler'|'printarea'|'gl'|'contour';
type NodeX=DrainNode&{ntype?:string;labelOff?:Off};
type LinkX=DrainLink&{vertices?:Pt[];labelOff?:Off};
type Alloc={linkId:string;pct:number};
type GCatch={id:string;name:string;area:number;surfaceType:string;C:number;H:number;L:number;
  poly?:Pt[];allocs:Alloc[]};
type Snapshot={nodes:NodeX[];links:LinkX[];gcatch:GCatch[];glPts:GLPoint[];glCtrs:GLContour[]};
type Measure={id:string;a:Pt;b:Pt};
type Sel={kind:'node'|'link'|'measure'|'catch'|'contour';id:string};
type ImgItem={id:string;name:string;el:HTMLImageElement|null;e:number;n:number;mpp:number;
  opacity:number;locked:boolean;above:boolean;visible:boolean;src?:string};   // src: URL / data URL that reloads itself

const CP_TYPES=['Standard Catchpit','Catchpit with Trap'];
const MH_TYPES=['Manhole Type D1','Manhole Type E1','Manhole Type F1'];
const TOOL_LABELS:Record<Tool,string>={select:'Select',manhole:'Manhole',catchpit:'Catchpit',
  outfall:'Outfall',connect:'UC/Pipe',catchment:'Catchment',gl:'GL Spot',contour:'Contour',
  calibrate:'Calibrate',ruler:'Ruler',printarea:'Print area'};

const TOOL_GROUPS:{id:string;title:string;tools:Tool[]}[]=[
  {id:'net',title:'Network & Nodes',tools:['select','manhole','catchpit','outfall','connect']},
  {id:'ter',title:'Catchment & Terrain',tools:['catchment','gl','contour']},
  {id:'utl',title:'Drawing & Utilities',tools:['calibrate','ruler','printarea']},
];

const DEFAULT_THEME={
  bg:'#020617',grid:'#1e293b',channel:'#1e40af',pipe:'#e879f9',selected:'#22d3ee',
  catch:'#34d399',measure:'#a3e635',text:'#cbd5e1',
  manhole:'#38bdf8',catchpit:'#fbbf24',outfall:'#f87171',
  nodeScale:1,lineWidth:0.8,textSize:4,
  show:{labels:true,usage:false,velocity:false,flow:false,size:false,
    nodes:true,links:true,catch:true,images:true}};
type Theme=typeof DEFAULT_THEME;

const ALL_CONDS:Condition[]=['Best','Good','Fair','Bad'];
const condsFor=(tbl:any,mat:string)=>ALL_CONDS.filter(c=>tbl[mat]?.[c]!=null);
const migrateCond=(tbl:any,mat:string,cur:Condition):Condition=>{
  const cs=condsFor(tbl,mat);
  return cs.includes(cur)?cur:cs.includes('Good')?'Good':(cs[0]??'Good'); };

const AUTO_LABEL=/^(UC|P)\d+$/;
const relabel=(ls:LinkX[])=>{let uc=0,p=0;
  return ls.map(l=>AUTO_LABEL.test(l.label)||l.label===''
    ?{...l,label:l.section.type==='circular'?`P${++p}`:`UC${++uc}`}:l); };

const shoelace=(pts:Pt[])=>Math.abs(pts.reduce((a,p,i)=>{
  const q=pts[(i+1)%pts.length]; return a+p.e*q.n-q.e*p.n; },0))/2;
const maxPairDist=(pts:Pt[])=>{let m=0;
  for(let i=0;i<pts.length;i++)for(let j=i+1;j<pts.length;j++)
    m=Math.max(m,Math.hypot(pts[i].e-pts[j].e,pts[i].n-pts[j].n)); return m; };
const pathLen=(pts:Pt[])=>pts.reduce((s,p,i)=>i?s+Math.hypot(p.e-pts[i-1].e,p.n-pts[i-1].n):0,0);
const midOfPath=(pts:Pt[]):Pt=>{let t=pathLen(pts)/2;
  for(let i=1;i<pts.length;i++){const d=Math.hypot(pts[i].e-pts[i-1].e,pts[i].n-pts[i-1].n);
    if(t<=d&&d>0)return{e:pts[i-1].e+(pts[i].e-pts[i-1].e)*t/d,n:pts[i-1].n+(pts[i].n-pts[i-1].n)*t/d};
    t-=d;}
  return pts[pts.length-1];};
const distSeg=(p:Pt,a:Pt,b:Pt)=>{const dx=b.e-a.e,dy=b.n-a.n;const L2=dx*dx+dy*dy;
  if(L2<1e-12)return Math.hypot(p.e-a.e,p.n-a.n);
  let t=((p.e-a.e)*dx+(p.n-a.n)*dy)/L2;t=Math.max(0,Math.min(1,t));
  return Math.hypot(p.e-a.e-t*dx,p.n-a.n-t*dy);};
const centroid=(pts:Pt[]):Pt=>({e:pts.reduce((s,p)=>s+p.e,0)/pts.length,
  n:pts.reduce((s,p)=>s+p.n,0)/pts.length});

const Svg=(p:any)=><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
  strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" {...p}/>;
const IconSave=()=><Svg><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></Svg>;
const IconSaveAs=()=><Svg><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M12 11v6m-3-3h6"/></Svg>;
const IconOpen=()=><Svg><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></Svg>;
const IconUndo=()=><Svg><path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"/></Svg>;
const IconRedo=()=><Svg><path d="m15 14 5-5-5-5"/><path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13"/></Svg>;
const IconDel=()=><Svg><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></Svg>;
const IconImg=()=><Svg><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></Svg>;
const IconGrid=()=><Svg><rect x="3" y="3" width="18" height="18" rx="1"/><path d="M9 3v18M15 3v18M3 9h18M3 15h18"/></Svg>;
const IconGear=()=><Svg><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></Svg>;
const RibbonBtn=({title,onClick,disabled,active,children}:any)=>(
  <button title={title} onClick={onClick} disabled={disabled}
    className={`p-2 rounded border ${active?'bg-blue-600 border-blue-400':'border-slate-700 bg-slate-800 hover:bg-slate-700'} disabled:opacity-30`}>{children}</button>);

export default function DrainageEditor({ onBack }: { onBack?: () => void } = {}){
  const [tab,setTab]=useState<'editor'|'tables'>('editor');
  const [nodes,setNodes]=useState<NodeX[]>([]);
  const [links,setLinks]=useState<LinkX[]>([]);
  const [gcatch,setGCatch]=useState<GCatch[]>([]);
  const [measures,setMeasures]=useState<Measure[]>([]);
  const [images,setImages]=useState<ImgItem[]>([]);
  const [selImgId,setSelImgId]=useState<string|null>(null);
  const [theme,setTheme]=useState<Theme>(DEFAULT_THEME);
  const [showProps,setShowProps]=useState(false);
  const [gridOn,setGridOn]=useState(true);
  const [gridStep,setGridStep]=useState(0);
  const [tool,setTool]=useState<Tool>('select');
  const [sel,setSel]=useState<Sel|null>(null);
  const [selectedIds,setSelectedIds]=useState<Set<string>>(()=>new Set());
  const [hoverId,setHoverId]=useState<string|null>(null);
  const [pending,setPending]=useState<{nodeId?:string;point?:Pt;verts:Pt[]}|null>(null);
  const [draftPoly,setDraftPoly]=useState<Pt[]>([]);
  const [settings,setSettings]=useState<DesignSettings>({returnPeriod:200,designType:'slope',stormRegion:'HKO',climatePeriod:'end21'});
  const [view,setView]=useState({x:400,y:300,scale:8});
  const [size,setSize]=useState({w:900,h:640});
  const [shiftDown,setShiftDown]=useState(false);
  const [marquee,setMarquee]=useState<{x:number;y:number;w:number;h:number}|null>(null);
  const [panelW,setPanelW]=useState(384);
  const wrapRef=useRef<HTMLDivElement>(null);
  const fileRef=useRef<HTMLInputElement>(null);
  const imgFileRef=useRef<HTMLInputElement>(null);
  const [calPts,setCalPts]=useState<Pt[]>([]);
  const [rulPts,setRulPts]=useState<Pt[]>([]);
  const [printArea,setPrintArea]=useState<{e0:number;n0:number;e1:number;n1:number}|null>(null);
  const [paPts,setPaPts]=useState<Pt[]>([]);
  const [pdfOpen,setPdfOpen]=useState(false);
  const [pdfOpts,setPdfOpts]=useState<PdfOpts>({paper:'A3',landscape:true,scale:'fit'});
  const [proj,setProj]=useState({job:'',jobNo:'',by:'',subject:'Surface Drainage System Design Checking'});
  const [cursor,setCursor]=useState<Pt|null>(null);
  const [glPts,setGlPts]=useState<GLPoint[]>([]);
  const [glCtrs,setGlCtrs]=useState<GLContour[]>([]);
  const [draftCtr,setDraftCtr]=useState<Pt[]>([]);
  const [optOpen,setOptOpen]=useState(false);
  const [optP,setOptP]=useState<OptParams>({...DEFAULT_OPT_PARAMS});

  // UI Collapsible States
  const [openGroups,setOpenGroups]=useState<Record<string,boolean>>({net:true,ter:true,utl:true});
  const [projOpen,setProjOpen]=useState(false);
  const [designOpen,setDesignOpen]=useState(true);
  const [helpOpen,setHelpOpen]=useState(false);

  const selImg=images.find(i=>i.id===selImgId)??null;

  // ---- history (nodes + links + catchments) ----
  const [past,setPast]=useState<Snapshot[]>([]);
  const [future,setFuture]=useState<Snapshot[]>([]);
  const skipHist=useRef(false); const inited=useRef(false);
  const prevSnap=useRef<Snapshot>({nodes:[],links:[],gcatch:[],glPts:[],glCtrs:[]});
  const draggingRef=useRef(false); const dragStartSnap=useRef<Snapshot>({nodes:[],links:[],gcatch:[],glPts:[],glCtrs:[]});
  useEffect(()=>{
    if(!inited.current){inited.current=true;prevSnap.current={nodes,links,gcatch,glPts,glCtrs};return;}
    if(skipHist.current){skipHist.current=false;prevSnap.current={nodes,links,gcatch,glPts,glCtrs};return;}
    if(draggingRef.current){prevSnap.current={nodes,links,gcatch,glPts,glCtrs};return;}
    setPast(p=>[...p,prevSnap.current].slice(-60)); setFuture([]);
    prevSnap.current={nodes,links,gcatch,glPts,glCtrs};
  },[nodes,links,gcatch,glPts,glCtrs]);
  const undo=()=>setPast(p=>{if(!p.length)return p;const prev=p[p.length-1];
    setFuture(f=>[{nodes,links,gcatch,glPts,glCtrs},...f].slice(0,60));
    skipHist.current=true;setNodes(prev.nodes);setLinks(prev.links);setGCatch(prev.gcatch);
    setGlPts(prev.glPts);setGlCtrs(prev.glCtrs);prevSnap.current=prev;
    setSel(null);setSelectedIds(new Set());return p.slice(0,-1);});
  const redo=()=>setFuture(f=>{if(!f.length)return f;const next=f[0];
    setPast(p=>[...p,{nodes,links,gcatch,glPts,glCtrs}].slice(-60));
    skipHist.current=true;setNodes(next.nodes);setLinks(next.links);setGCatch(next.gcatch);
    setGlPts(next.glPts);setGlCtrs(next.glCtrs);prevSnap.current=next;
    setSel(null);setSelectedIds(new Set());return f.slice(1);});

  // ---- save / open ----
  const fileHandleRef=useRef<any>(null);
  const [fileName,setFileName]=useState<string|null>(null);
  const [saveMsg,setSaveMsg]=useState<string|null>(null);
  const flash=(m:string)=>{setSaveMsg(m);window.setTimeout(()=>setSaveMsg(null),2500);};
  const serialize=()=>JSON.stringify({version:5,nodes,links,gcatch,glPts,glCtrs,settings,measures,theme,
    grid:{on:gridOn,step:gridStep},
    printArea,proj,optParams:optP,
    images:images.map(({el,...meta})=>meta)},null,2);
  const loadData=(d:any)=>{
    setNodes(d.nodes||[]);
    setLinks((d.links||[]).map((l:any)=>({...l,catchments:[]})));
    if(d.version>=4)setGCatch(d.gcatch||[]);
    else{
      const gs:GCatch[]=[];
      (d.links||[]).forEach((l:any)=>(l.catchments||[]).forEach((c:any)=>gs.push({
        id:c.id||uid('A'),name:c.id||'A?',area:c.area,surfaceType:c.surfaceType,
        C:c.C,H:c.H,L:c.L,poly:c.poly,allocs:[{linkId:l.id,pct:100}]})));
      setGCatch(gs);}
    setGlPts(d.glPts||[]);
    setGlCtrs(d.glCtrs||[]);
    setDraftCtr([]);
    setMeasures(d.measures||[]);
    if(d.settings)setSettings(d.settings);
    if(d.theme)setTheme(()=>({...DEFAULT_THEME,...d.theme,show:{...DEFAULT_THEME.show,...(d.theme.show||{})}}));
    if(d.grid){setGridOn(!!d.grid.on);setGridStep(d.grid.step??0);}
    if(d.printArea)setPrintArea(d.printArea);
    if(d.proj)setProj(p=>({...p,...d.proj}));
    if(d.optParams)setOptP(p=>({...p,...d.optParams}));
    if(d.images){
      const metas:ImgItem[]=d.images.map((m:any)=>({...m,el:null}));
      setImages(metas);
      metas.forEach(m=>{if(!m.src)return;
        const el=new Image();el.crossOrigin='anonymous';
        el.onload=()=>setImages(ls=>ls.map(x=>x.id===m.id&&!x.el?{...x,el}:x));
        el.src=m.src;});}
    else if(d.imgCal)setImages([{id:uid('I'),name:'background',el:null,e:d.imgCal.e,n:d.imgCal.n,
      mpp:d.imgCal.mpp??0.5,opacity:d.imgCal.opacity??0.5,locked:true,above:false,visible:true}]);
    setSelImgId(null);setPending(null);setDraftPoly([]);
    setSel(null);setSelectedIds(new Set());setPast([]);setFuture([]);
    skipHist.current=true;};

// ---- Auto-load AI Copilot / MCP generated network if handed off from Chat ----
  // v3.1.1: optional `autoOptimise: true` (+ `optParams`) runs the shared engine optimiser before loading.
  useEffect(() => {
    try {
      const rawHandoff = localStorage.getItem('civeng_drainage_mcp_handoff_v1');
      if (rawHandoff) {
        const parsed = JSON.parse(rawHandoff);
        if (parsed && parsed.version === 5) {
          let data = parsed;
          let note = 'Loaded AI Copilot drainage design – click Optimise or edit freely';
          if (parsed.autoOptimise && Array.isArray(parsed.links) && parsed.links.length) {
            const p: OptParams = { ...DEFAULT_OPT_PARAMS, ...(parsed.optParams || {}) };
            const r = optimiseNetwork(parsed.nodes || [], parsed.links.map((l: any) => ({ ...l, catchments: [] })),
              parsed.gcatch || [], parsed.settings || settings, p);
            if (r.diagnostics.length) console.info('Optimise diagnostics:\n' + r.diagnostics.join('\n'));
            data = { ...parsed, nodes: r.nodes, links: r.links, optParams: p };
            note = r.failed.length ? `Loaded and optimised — ⚠ did not converge: ${r.failed.join(', ')}`
              : 'Loaded and optimised — all sections within limits ✓';
          }
          loadData(data);
          flash(note);
        }
        setTimeout(() => localStorage.removeItem('civeng_drainage_mcp_handoff_v1'), 300);
      }
    } catch (err) {
      console.warn('MCP drainage handoff skipped:', err);
    }
  }, []);

  const doSave=async(forceDialog=false)=>{
    const data=serialize();
    const fsa=(window as any).showSaveFilePicker;
    if(fsa){
      try{
        if(forceDialog||!fileHandleRef.current){
          fileHandleRef.current=await fsa({suggestedName:fileName??'drainage-design.json',
            types:[{description:'Drainage design',accept:{'application/json':['.json']}}]});
        }
        const w=await fileHandleRef.current.createWritable();
        await w.write(data);await w.close();
        setFileName(fileHandleRef.current.name);
        flash(`Saved → ${fileHandleRef.current.name}`);return;
      }catch(err:any){
        if(err?.name==='AbortError')return;
        fileHandleRef.current=null;
      }
    }
    try{
      const blob=new Blob([data],{type:'application/json'});
      const url=URL.createObjectURL(blob);const a=document.createElement('a');
      a.href=url;a.download=fileName??'drainage-design.json';
      document.body.appendChild(a);a.click();a.remove();
      window.setTimeout(()=>URL.revokeObjectURL(url),1000);
      flash('Downloaded (browser fallback)');
    }catch{flash('⚠ Save blocked by sandbox — run app locally');}
  };
  const doOpen=async()=>{
    const fsa=(window as any).showOpenFilePicker;
    if(fsa){
      try{
        const [h]=await fsa({types:[{description:'Drainage design',accept:{'application/json':['.json']}}]});
        fileHandleRef.current=h;setFileName(h.name);
        const f=await h.getFile();loadData(JSON.parse(await f.text()));
        flash(`Opened ${h.name} — Ctrl+S writes back to it`);return;
      }catch(err:any){if(err?.name==='AbortError')return;}
    }
    fileRef.current?.click();
  };
  const onFile=(e:React.ChangeEvent<HTMLInputElement>)=>{const f=e.target.files?.[0];if(!f)return;
    const r=new FileReader();r.onload=()=>{try{loadData(JSON.parse(String(r.result)));
      setFileName(f.name);flash(`Opened ${f.name}`);}catch{flash('⚠ Invalid file');}};
    r.readAsText(f);e.target.value='';};

  // ---- PDF / Excel export ----
  const buildPrims=():Prim[]=>{
    const prims:Prim[]=[];
    images.filter(im=>im.el&&im.visible!==false).forEach(im=>{
      prims.push({k:'img',el:im.el!,e:im.e,n:im.n,
        wM:im.el!.width*im.mpp,hM:im.el!.height*im.mpp,opacity:im.opacity??1});});
    gcatch.filter(g=>g.poly).forEach(g=>{
      prims.push({k:'poly',pts:g.poly!,close:true,fill:theme.catch,fillA:0.15,stroke:'#059669',w:0.2});
      const c=centroid(g.poly!);
      prims.push({k:'text',e:c.e,n:c.n,s:g.name,pt:8,color:'#047857'});});
    links.forEach(l=>{const path=linkPath(l);if(!path)return;
      const pipe=l.section.type==='circular';
      prims.push({k:'poly',pts:path,stroke:pipe?'#a21caf':'#334155',w:0.35,
        dash:pipe?[1.2,1]:undefined,arrow:true});
      const m=midOfPath(path);
      prims.push({k:'text',e:m.e,n:m.n,s:linkAnnot(l),pt:7,color:'#111111'});});
    nodes.forEach(n=>{
      prims.push({k:'node',e:n.e,n:n.n,rMM:2,fill:(theme as any)[n.type],
        shape:n.type==='catchpit'?'square':n.type==='outfall'?'tri':'circle'});
      if(theme.show.labels)prims.push({k:'text',e:n.e+0.5,n:n.n+0.5,s:n.label,pt:7,color:'#111111'});});
    measures.forEach(m=>{
      prims.push({k:'poly',pts:[m.a,m.b],stroke:'#65a30d',w:0.2,dash:[1,1]});
      prims.push({k:'text',e:(m.a.e+m.b.e)/2,n:(m.a.n+m.b.n)/2,
        s:Math.hypot(m.b.e-m.a.e,m.b.n-m.a.n).toFixed(2)+' m',pt:6,color:'#3f6212'});});
    return prims;};

  const doPdf=()=>{if(!printArea){flash('Draw a print area first');return;}
    exportPdf(buildPrims(),printArea,pdfOpts);setPdfOpen(false);};

  const CLIMATE_WORDING:any={before2041:'period before 2041',
    mid21:'mid 21st century (2041-2060)',end21:'end of 21 century (2081-2100)'};
  const stormTitle=()=>settings.designType==='slope'
    ?'GEO Technical Guidance Note No, 30 (TGN 30) Table 1 (May 2023)'
    :({HKO:'Table 3a  Storm Constants for Different Return Periods of HKO Headquarters (Corr 1/2024)',
       TaiMoShan:'Table 3b – Storm Constants for Different Return Periods of Tai Mo Shan Area (2018)',
       WestLantau:'Table 3c – Storm Constants for Different Return Periods of West Lantau Area (2018)',
       NorthDistrict:'Table 3d – Storm Constants for Different Return Periods of North District Area (Corr 1/2024)',
       DrySeasonWhole:'Table 3 – Storm Constants, Whole Dry Season (Nov-Mar), HKO (sensitivity check)',
       DrySeasonCore:'Table 4 – Storm Constants, Core Dry Season (Dec-Feb), HKO (sensitivity check)'} as any)[settings.stormRegion];

  const doXlsx=async()=>{
    const mode=(pipe:boolean)=>{const c=new Map<string,number>();
      links.filter(l=>(l.section.type==='circular')===pipe)
        .forEach(l=>c.set(l.material+'|'+l.condition,(c.get(l.material+'|'+l.condition)??0)+1));
      const top=[...c.entries()].sort((a,b)=>b[1]-a[1])[0]?.[0];
      return top?top.split('|'):pipe
        ?['Concrete - Precast concrete pipes with ‘O’ ring joints','Fair']
        :['Concrete-lined channels','Fair'];};
    const [mMat,mCond]=mode(false),[cMat,cCond]=mode(true);
    const rows=links.map(l=>({label:l.label,pipe:l.section.type==='circular',
      size:l.section.width,
      us:l.fromNodeId?nodeById.get(l.fromNodeId)!.label:'-',
      ds:l.toNodeId?nodeById.get(l.toNodeId)!.label:'-',
      len:+lengthOf(l).toFixed(2),usIL:l.usInvert,dsIL:l.dsInvert,
      nPipes:(l as any).nPipes??1,usGL:usGLOf(l)??0,dsGL:dsGLOf(l)??0,
      material:l.material,condition:l.condition,
      catchAllocs:gcatch.filter(g=>g.allocs.some(a=>a.linkId===l.id&&a.pct>0))
        .map(g=>({name:g.name,pct:g.allocs.find(a=>a.linkId===l.id)!.pct})),
      upstream:links.filter(u=>u.toNodeId&&u.toNodeId===l.fromNodeId).map(u=>u.label)}));
    const cs=gcatch.map(g=>({name:g.name,area:g.area,surface:g.surfaceType,C:g.C,H:g.H,L:g.L}));
    try{
      const res=await exportExcel({job:proj.job,jobNo:proj.jobNo,by:proj.by,subject:proj.subject,
        climateWording:CLIMATE_WORDING[settings.climatePeriod],returnPeriod:settings.returnPeriod,
        stormTableTitle:stormTitle(),manningMat:mMat,manningCond:mCond,cwMat:cMat,cwCond:cCond},rows,cs);
      if(res.warnings.length)console.warn('XLSX export warnings:\n'+res.warnings.join('\n'));
      flash(res.files>1?`Exported ${res.files} workbooks (capacity split)`
        :res.warnings.length?`Exported — ${res.warnings.length} warning(s), see console`:'Exported workbook');
    }catch(e){console.error(e);flash('⚠ XLSX export failed — is /template.xlsx present?');}};

  // ---- image + PDF import ----
  const addImgEl=(name:string,el:HTMLImageElement)=>setImages(ls=>{
    const idx=ls.findIndex(x=>!x.el&&x.name===name);
    if(idx>=0){const cp=[...ls];cp[idx]={...cp[idx],el};setSelImgId(cp[idx].id);return cp;}
    const it:ImgItem={id:uid('I'),name,el,e:ORIGIN.e,n:ORIGIN.n,mpp:0.5,
      opacity:0.5,locked:false,above:false,visible:true};
    setSelImgId(it.id);return [...ls,it];});
  const importPdf=async(f:File)=>{
    try{
      flash(`Rendering ${f.name}…`);
      const pdf=await pdfjsLib.getDocument({data:await f.arrayBuffer()}).promise;
      for(let p=1;p<=pdf.numPages;p++){
        const page=await pdf.getPage(p);
        const vp1=page.getViewport({scale:1});
        const scale=Math.min(4096/Math.max(vp1.width,vp1.height),3);
        const vp=page.getViewport({scale});
        const canvas=document.createElement('canvas');
        canvas.width=Math.ceil(vp.width);canvas.height=Math.ceil(vp.height);
        await page.render({canvasContext:canvas.getContext('2d')!,viewport:vp} as any).promise;
        const name=pdf.numPages>1?`${f.name} p${p}`:f.name;
        const el=new Image();
        el.onload=()=>addImgEl(name,el);
        el.src=canvas.toDataURL('image/png');
      }
      flash(`Imported ${pdf.numPages} page(s) from ${f.name} — use Calibrate to scale`);
    }catch{flash('⚠ Could not read PDF');}
  };
  const onImgFile=(e:React.ChangeEvent<HTMLInputElement>)=>{
    const files=Array.from(e.target.files||[]);
    files.forEach(f=>{
      if(f.type==='application/pdf'||/\.pdf$/i.test(f.name)){importPdf(f);return;}
      const el=new Image();
      el.onload=()=>addImgEl(f.name,el);
      el.src=URL.createObjectURL(f);});
    e.target.value='';};
  const updImg=(id:string,patch:Partial<ImgItem>)=>setImages(ls=>ls.map(i=>i.id===id?{...i,...patch}:i));
  const moveImg=(id:string,dir:-1|1)=>setImages(ls=>{
    const i=ls.findIndex(x=>x.id===id);const j=i+dir;
    if(i<0||j<0||j>=ls.length)return ls;
    const cp=[...ls];[cp[i],cp[j]]=[cp[j],cp[i]];return cp;});
  const removeImg=(id:string)=>{setImages(ls=>ls.filter(i=>i.id!==id));
    if(selImgId===id)setSelImgId(null);};

  useEffect(()=>{const el=wrapRef.current;if(!el)return;
    const ro=new ResizeObserver(()=>setSize({w:el.clientWidth,h:el.clientHeight}));
    ro.observe(el);return()=>ro.disconnect();},[panelW,tab]);

  const nodeById=useMemo(()=>new Map(nodes.map(n=>[n.id,n])),[nodes]);
  const endOf=(l:LinkX)=>({from:l.fromNodeId?nodeById.get(l.fromNodeId):l.fromPoint,
    to:l.toNodeId?nodeById.get(l.toNodeId):l.toPoint});
  const linkPath=(l:LinkX):Pt[]|null=>{const{from,to}=endOf(l);
    return from&&to?[{e:from.e,n:from.n},...(l.vertices??[]),{e:to.e,n:to.n}]:null;};
  const lengthOf=(l:LinkX)=>{const p=linkPath(l);return p?pathLen(p):0;};
  const usGLOf=(l:LinkX)=>l.fromNodeId?nodeById.get(l.fromNodeId)?.groundLevel:l.usGroundLevel;
  const dsGLOf=(l:LinkX)=>l.toNodeId?nodeById.get(l.toNodeId)?.groundLevel:l.dsGroundLevel;

  // ---- % allocations -> per-link equivalent catchments (shared with the engine sidecar) ----
  const compLinks=useMemo(()=>withAllocatedCatchments(links,gcatch),[links,gcatch]);
  const results=useMemo(()=>{try{return computeNetwork(nodes,compLinks,settings);}
    catch{return new Map();}},[nodes,compLinks,settings]);
  const runOptimize=()=>{
    if(!links.length){flash('Nothing to optimise');return;}
    const r=optimiseNetwork(nodes,links,gcatch,settings,optP);
    if(r.diagnostics.length)console.info('Optimise diagnostics:\n'+r.diagnostics.join('\n'));
    setNodes(r.nodes);setLinks(r.links);setOptOpen(false);
    flash(r.failed.length?`Optimised — ⚠ did not converge: ${r.failed.join(', ')} (reasons in console)`
      :'Optimised — all sections within limits ✓');};

  // ---- DSD node types ----
  const autoNodeType=(n:NodeX):string=>{
    if(n.type==='manhole'){const d=n.groundLevel-n.invertLevel;
      return d<=1.5?'Manhole Type D1':d<=3?'Manhole Type E1':'Manhole Type F1';}
    if(n.type==='catchpit'){
      const toMH=links.some(l=>l.fromNodeId===n.id&&l.toNodeId
        &&nodeById.get(l.toNodeId)?.type==='manhole');
      return toMH?'Catchpit with Trap':'Standard Catchpit';}
    return '—';};
  const effNodeType=(n:NodeX)=>n.ntype??autoNodeType(n);

  const stageToWorld=(sx:number,sy:number)=>baseToWorld((sx-view.x)/view.scale,(sy-view.y)/view.scale);
  const justMarquee=useRef(false);
  const clickTol=()=>10/view.scale;

  const gridLines=useMemo(()=>{
    if(!gridOn)return [];
    const w0=stageToWorld(0,0), w1=stageToWorld(size.w,size.h);
    const eMin=Math.min(w0.e,w1.e), eMax=Math.max(w0.e,w1.e);
    const nMin=Math.min(w0.n,w1.n), nMax=Math.max(w0.n,w1.n);
    let step=gridStep>0?gridStep
      :([0.1,0.25,0.5,1,2,5,10,25,50,100,250,500,1000].find(c=>c*view.scale>=16)??1000);
    while((eMax-eMin)/step>400)step*=5;
    const major=step*5;
    const isMajor=(v:number)=>Math.abs(v/major-Math.round(v/major))<1e-6;
    const out:React.ReactElement[]=[];let k=0;
    for(let e=Math.floor(eMin/step)*step;e<=eMax;e+=step){
      const a=worldToBase(e,nMin),b=worldToBase(e,nMax);
      out.push(<Line key={'g'+(k++)} points={[a.x,a.y,b.x,b.y]} stroke={theme.grid}
        strokeWidth={(isMajor(e)?1.2:0.5)/view.scale} listening={false}/>);}
    for(let n=Math.floor(nMin/step)*step;n<=nMax;n+=step){
      const a=worldToBase(eMin,n),b=worldToBase(eMax,n);
      out.push(<Line key={'g'+(k++)} points={[a.x,a.y,b.x,b.y]} stroke={theme.grid}
        strokeWidth={(isMajor(n)?1.2:0.5)/view.scale} listening={false}/>);}
    return out;
  },[gridOn,gridStep,view,size,theme.grid]);

  const makeLink=(from:{nodeId?:string;point?:Pt},to:{nodeId?:string;point?:Pt},verts:Pt[]=[])=>{
    const fn=from.nodeId?nodeById.get(from.nodeId)!:null;
    const tn=to.nodeId?nodeById.get(to.nodeId)!:null;
    const fp=fn??from.point!, tp=tn??to.point!;
    const clean=verts.filter(v=>Math.hypot(v.e-fp.e,v.n-fp.n)>0.05&&Math.hypot(v.e-tp.e,v.n-tp.n)>0.05);
    const usInv=fn?fn.invertLevel:100;
    const l:LinkX={id:uid('L'),label:'',
      fromNodeId:from.nodeId??null,fromPoint:from.nodeId?undefined:from.point,
      toNodeId:to.nodeId??null,toPoint:to.nodeId?undefined:to.point,
      vertices:clean,
      section:{type:'uchannel',width:300},material:'Concrete-lined channels',
      condition:'Fair',n:0.016,ks:0.6,usInvert:usInv,
      dsInvert:tn?tn.invertLevel:usInv-0.5,
      usGroundLevel:fn?undefined:usInv+1.5, dsGroundLevel:tn?undefined:(tn?0:usInv+1),
      catchments:[]};
    setLinks(p=>relabel([...p,l]));setPending(null);
    setSel({kind:'link',id:l.id});setSelectedIds(new Set([l.id]));};

  const selLink=sel?.kind==='link'?links.find(l=>l.id===sel.id):undefined;
  const selNode=sel?.kind==='node'?nodes.find(n=>n.id===sel.id):undefined;
  const selMeasure=sel?.kind==='measure'?measures.find(m=>m.id===sel.id):undefined;
  const selCtr=sel?.kind==='contour'?glCtrs.find(c=>c.id===sel.id):undefined;
  const selCatch=sel?.kind==='catch'?gcatch.find(c=>c.id===sel.id):undefined;

  const setNodeLevel=(id:string,patch:{groundLevel?:number;invertLevel?:number})=>{
    setNodes(p=>p.map(n=>n.id===id?{...n,...patch}:n));
    if(patch.invertLevel!=null){const v=patch.invertLevel;
      setLinks(p=>p.map(l=>{let o=l;
        if(l.fromNodeId===id&&l.usInvert!==v)o={...o,usInvert:v};
        if(l.toNodeId===id&&l.dsInvert!==v)o={...o,dsInvert:v};
        return o;}));}};

  const commitCatchment=(polyIn:Pt[])=>{
    const pts=polyIn.filter((p,i,a)=>i===0||Math.hypot(p.e-a[i-1].e,p.n-a[i-1].n)>0.05);
    if(pts.length<3){setDraftPoly([]);return;}
    const g:GCatch={id:uid('A'),name:'A'+(gcatch.length+1),
      area:Math.round(shoelace(pts)),surfaceType:'Paved',C:0.95,H:0.5,
      L:Math.round(maxPairDist(pts)*10)/10,poly:pts,
      allocs:selLink?[{linkId:selLink.id,pct:100}]:[]};
    setGCatch(p=>[...p,g]);setDraftPoly([]);
    setSel({kind:'catch',id:g.id});setSelectedIds(new Set([g.id]));};
  const finishCatchment=()=>{
    if(tool!=='catchment'||draftPoly.length<3){setDraftPoly([]);return;}
    commitCatchment(draftPoly);};
  const finishContour=()=>{
    if(tool!=='contour'||draftCtr.length<2){setDraftCtr([]);return;}
    const z=parseFloat(window.prompt('Contour level (mPD):')||'');
    const pts=draftCtr.filter((p,i,a)=>i===0||Math.hypot(p.e-a[i-1].e,p.n-a[i-1].n)>0.05);
    if(isFinite(z)&&pts.length>=2)setGlCtrs(p=>[...p,{id:uid('C'),z,pts}]);
    setDraftCtr([]);};
  const autoCalcGL=()=>{
  const interp=buildInterpolator(glPts,glCtrs);
  if(!interp){flash('Add GL points / contours first');return;}
  const r1=(v:number)=>Math.round(v*10)/10;
  setNodes(p=>p.map(n=>({...n,groundLevel:r1(interp(n.e,n.n))})));
  setLinks(p=>p.map(l=>({...l,
    usGroundLevel:l.fromNodeId?l.usGroundLevel:l.fromPoint?r1(interp(l.fromPoint.e,l.fromPoint.n)):l.usGroundLevel,
    dsGroundLevel:l.toNodeId?l.dsGroundLevel:l.toPoint?r1(interp(l.toPoint.e,l.toPoint.n)):l.dsGroundLevel})));
  flash('GL assigned to all nodes from terrain — one-off; edit freely afterwards');};
  const finishConnect=()=>{
    if(tool!=='connect'||!pending)return;
    if(pending.verts.length===0){setPending(null);return;}
    const last=pending.verts[pending.verts.length-1];
    makeLink(pending,{point:last},pending.verts.slice(0,-1));};

  const onStageClick=(e:any)=>{
    if(justMarquee.current){justMarquee.current=false;return;}
    const stage=e.target.getStage(); const pos=stage.getPointerPosition();
    const w=stageToWorld(pos.x,pos.y);
    if(tool==='calibrate'){
      if(!selImg?.el)return;
      const pts=[...calPts,{e:w.e,n:w.n}];
      if(pts.length<2){setCalPts(pts);return;}
      const d=Math.hypot(pts[1].e-pts[0].e,pts[1].n-pts[0].n); setCalPts([]);
      const D=parseFloat(window.prompt(`Segment measures ${d.toFixed(2)} m. Enter TRUE distance (m):`)||'');
      if(!isFinite(D)||D<=0||d<1e-6)return;
      const f=D/d,A=pts[0],id=selImg.id;
      setImages(ls=>ls.map(im=>im.id!==id?im
        :{...im,mpp:im.mpp*f,e:A.e+(im.e-A.e)*f,n:A.n+(im.n-A.n)*f}));
      return;}
    if(tool==='ruler'){
      if(rulPts.length===0)setRulPts([{e:w.e,n:w.n}]);
      else{setMeasures(m=>[...m,{id:uid('M'),a:rulPts[0],b:{e:w.e,n:w.n}}]);setRulPts([]);}
      return;}
    if(tool==='printarea'){
      if(paPts.length===0)setPaPts([{e:w.e,n:w.n}]);
      else{setPrintArea({e0:paPts[0].e,n0:paPts[0].n,e1:w.e,n1:w.n});setPaPts([]);setTool('select');}
      return;}
    if(tool==='gl'){
      const z=parseFloat(window.prompt(`Ground level at E ${w.e.toFixed(1)} N ${w.n.toFixed(1)} (mPD):`)||'');
      if(isFinite(z))setGlPts(p=>[...p,{id:uid('G'),e:snap(w.e),n:snap(w.n),z}]);
      return;}
    if(tool==='contour'){
      setDraftCtr(p=>[...p,{e:snap(w.e),n:snap(w.n)}]);return;}
    if(tool==='catchment'){
      if(draftPoly.length===0){setDraftPoly([{e:snap(w.e),n:snap(w.n)}]);return;}
      if(draftPoly.length>=3&&Math.hypot(w.e-draftPoly[0].e,w.n-draftPoly[0].n)<=clickTol()){
        commitCatchment(draftPoly);return;}
      setDraftPoly(p=>[...p,{e:snap(w.e),n:snap(w.n)}]);return;}
    if(e.target!==stage)return;
    const E=snap(w.e),N=snap(w.n);
    if(tool==='manhole'||tool==='catchpit'||tool==='outfall'){
      const n:NodeX={id:uid('N'),type:tool,e:E,n:N,groundLevel:100,invertLevel:98.5,
        size:tool==='manhole'?1050:450,label:tool[0].toUpperCase()+(nodes.length+1)};
      setNodes(p=>[...p,n]);setSel({kind:'node',id:n.id});setSelectedIds(new Set([n.id]));}
    else if(tool==='connect'){
      if(!pending)setPending({point:{e:E,n:N},verts:[]});
      else setPending(p=>p?{...p,verts:[...p.verts,{e:E,n:N}]}:p);}
    else{setSel(null);setSelectedIds(new Set());}};

  const onNodeClick=(n:NodeX)=>{
    if(tool==='catchment'||tool==='calibrate'||tool==='ruler')return;
    if(tool==='connect'){
      if(!pending){setPending({nodeId:n.id,verts:[]});return;}
      if(pending.nodeId===n.id&&pending.verts.length===0){setPending(null);return;}
      makeLink(pending,{nodeId:n.id},pending.verts);}
    else{setSel({kind:'node',id:n.id});setSelectedIds(new Set([n.id]));}};

  const onWheel=(e:any)=>{e.evt.preventDefault();
    const stage=e.target.getStage();const p=stage.getPointerPosition();
    const ns=Math.min(80,Math.max(0.5,view.scale*(e.evt.deltaY>0?0.9:1.1)));
    const wx=(p.x-view.x)/view.scale,wy=(p.y-view.y)/view.scale;
    setView({scale:ns,x:p.x-wx*ns,y:p.y-wy*ns});};
  const onDragStartHist=()=>{draggingRef.current=true;dragStartSnap.current={nodes,links,gcatch,glPts,glCtrs};};
  const dragNode=(n:NodeX,e:any)=>{
    if(e.target!==e.currentTarget)return;
    const w=baseToWorld(e.target.x(),e.target.y());
    setNodes(p=>p.map(x=>x.id===n.id?{...x,e:snap(w.e),n:snap(w.n)}:x));};
  const onDragEndHist=()=>{draggingRef.current=false;
    setPast(p=>[...p,dragStartSnap.current].slice(-60));setFuture([]);
    prevSnap.current={nodes,links,gcatch,glPts,glCtrs};};

  const dragCatchVertex=(gid:string,vi:number,e:any)=>{
    const w=baseToWorld(e.target.x(),e.target.y());
    setGCatch(p=>p.map(g=>{
      if(g.id!==gid||!g.poly)return g;
      const poly=g.poly.map((pt,i)=>i===vi?{e:snap(w.e),n:snap(w.n)}:pt);
      return {...g,poly,area:Math.round(shoelace(poly)),L:Math.round(maxPairDist(poly)*10)/10};}));};
  const insertCatchVertex=(g:GCatch,w:Pt)=>{
    if(!g.poly)return;
    let best=0,bd=Infinity;const P=g.poly;
    for(let i=0;i<P.length;i++){const d=distSeg(w,P[i],P[(i+1)%P.length]);if(d<bd){bd=d;best=i;}}
    const poly=[...P.slice(0,best+1),{e:snap(w.e),n:snap(w.n)},...P.slice(best+1)];
    setGCatch(p=>p.map(x=>x.id!==g.id?x
      :{...x,poly,area:Math.round(shoelace(poly)),L:Math.round(maxPairDist(poly)*10)/10}));};
  const removeCatchVertex=(gid:string,vi:number)=>
    setGCatch(p=>p.map(g=>{
      if(g.id!==gid||!g.poly||g.poly.length<=3)return g;
      const poly=g.poly.filter((_,i)=>i!==vi);
      return {...g,poly,area:Math.round(shoelace(poly)),L:Math.round(maxPairDist(poly)*10)/10};}));
  const dragLinkVertex=(lid:string,vi:number,e:any)=>{
    const w=baseToWorld(e.target.x(),e.target.y());
    setLinks(p=>p.map(l=>l.id!==lid?l:{...l,
      vertices:(l.vertices??[]).map((v,i)=>i===vi?{e:snap(w.e),n:snap(w.n)}:v)}));};
  const insertLinkVertex=(l:LinkX,w:Pt)=>{
    const path=linkPath(l);if(!path)return;
    let best=0,bd=Infinity;
    for(let i=0;i<path.length-1;i++){const d=distSeg(w,path[i],path[i+1]);if(d<bd){bd=d;best=i;}}
    const verts=[...(l.vertices??[])];verts.splice(best,0,{e:snap(w.e),n:snap(w.n)});
    setLinks(p=>p.map(x=>x.id===l.id?{...x,vertices:verts}:x));};
  const removeLinkVertex=(lid:string,vi:number)=>
    setLinks(p=>p.map(l=>l.id!==lid?l:{...l,vertices:(l.vertices??[]).filter((_,i)=>i!==vi)}));
  const dragCtrVertex=(cid:string,vi:number,e:any)=>{
    const w=baseToWorld(e.target.x(),e.target.y());
    setGlCtrs(p=>p.map(c=>c.id!==cid?c:{...c,
      pts:c.pts.map((pt,i)=>i===vi?{e:snap(w.e),n:snap(w.n)}:pt)}));};
  const insertCtrVertex=(c:GLContour,w:Pt)=>{
    let best=0,bd=Infinity;
    for(let i=0;i<c.pts.length-1;i++){const d=distSeg(w,c.pts[i],c.pts[i+1]);if(d<bd){bd=d;best=i;}}
    setGlCtrs(p=>p.map(x=>x.id!==c.id?x:{...x,
      pts:[...x.pts.slice(0,best+1),{e:snap(w.e),n:snap(w.n)},...x.pts.slice(best+1)]}));};
  const removeCtrVertex=(cid:string,vi:number)=>
    setGlCtrs(p=>p.map(c=>c.id!==cid||c.pts.length<=2?c:{...c,pts:c.pts.filter((_,i)=>i!==vi)}));

  const marqueeStart=useRef<{x:number;y:number}|null>(null);
  const onStageMouseDown=(e:any)=>{
    if(tool!=='select'||!shiftDown)return;
    if(e.target!==e.target.getStage())return;
    const pos=e.target.getStage().getPointerPosition();
    marqueeStart.current={x:pos.x,y:pos.y};setMarquee({x:pos.x,y:pos.y,w:0,h:0});};
  const onStageMouseMove=(e:any)=>{
    const pos=e.target.getStage()?.getPointerPosition();
    if(pos)setCursor(stageToWorld(pos.x,pos.y));
    if(!marqueeStart.current||!pos)return;
    const s=marqueeStart.current;
    setMarquee({x:Math.min(s.x,pos.x),y:Math.min(s.y,pos.y),w:Math.abs(pos.x-s.x),h:Math.abs(pos.y-s.y)});};
  const onStageMouseUp=()=>{
    if(!marqueeStart.current)return;
    const m=marquee;marqueeStart.current=null;setMarquee(null);
    if(!m||(m.w<3&&m.h<3))return;
    const ids=new Set<string>();
    const scr=(bx:number,by:number)=>({x:bx*view.scale+view.x,y:by*view.scale+view.y});
    const inside=(p:{x:number;y:number})=>p.x>=m.x&&p.x<=m.x+m.w&&p.y>=m.y&&p.y<=m.y+m.h;
    nodes.forEach(n=>{const p=worldToBase(n.e,n.n);if(inside(scr(p.x,p.y)))ids.add(n.id);});
    links.forEach(l=>{const{from,to}=endOf(l);if(!from||!to)return;
      const a=worldToBase(from.e,from.n),b=worldToBase(to.e,to.n);
      if(inside(scr(a.x,a.y))||inside(scr(b.x,b.y)))ids.add(l.id);});
    measures.forEach(mm=>{const a=worldToBase(mm.a.e,mm.a.n),b=worldToBase(mm.b.e,mm.b.n);
      if(inside(scr(a.x,a.y))||inside(scr(b.x,b.y)))ids.add(mm.id);});
    setSelectedIds(ids);
    if(ids.size===1){const id=[...ids][0];
      setSel(nodes.some(n=>n.id===id)?{kind:'node',id}
        :links.some(l=>l.id===id)?{kind:'link',id}:{kind:'measure',id});}
    else setSel(null);
    justMarquee.current=true;};

  const updNode=(id:string,patch:Partial<NodeX>)=>setNodes(p=>p.map(n=>n.id===id?{...n,...patch}:n));
  const updLink=(id:string,patch:Partial<LinkX>)=>setLinks(p=>p.map(l=>l.id===id?{...l,...patch}:l));
  const updCatch=(id:string,patch:Partial<GCatch>)=>setGCatch(p=>p.map(g=>g.id===id?{...g,...patch}:g));
  const deleteSelection=()=>{
    const ids=new Set(selectedIds);if(sel)ids.add(sel.id);if(!ids.size)return;
    setNodes(p=>p.filter(n=>!ids.has(n.id)));
    setLinks(p=>relabel(p.filter(l=>!ids.has(l.id)&&!ids.has(l.fromNodeId||'')&&!ids.has(l.toNodeId||''))));
    setGCatch(p=>p.filter(g=>!ids.has(g.id))
      .map(g=>({...g,allocs:g.allocs.filter(a=>!ids.has(a.linkId))})));
    setMeasures(p=>p.filter(m=>!ids.has(m.id)));
    setGlCtrs(p=>p.filter(c=>!ids.has(c.id)));
    setSel(null);setSelectedIds(new Set());};

  const actionsRef=useRef<any>({});
  actionsRef.current={undo,redo,doSave,deleteSelection,
    enterKey:()=>{if(tool==='connect')finishConnect();else if(tool==='contour')finishContour();else finishCatchment();},
    escape:()=>{setPending(null);setCalPts([]);setRulPts([]);setDraftPoly([]);setPaPts([]);setDraftCtr([]);}};
  useEffect(()=>{
    const kd=(e:KeyboardEvent)=>{
      if(e.key==='Shift')setShiftDown(true);
      const ctrl=e.ctrlKey||e.metaKey;const t=e.target as HTMLElement;
      const editing=t&&(t.tagName==='INPUT'||t.tagName==='SELECT'||t.tagName==='TEXTAREA');
      const k=e.key.toLowerCase();
      if(ctrl&&k==='s'){e.preventDefault();actionsRef.current.doSave(e.shiftKey);return;}
      if(editing)return;
      if(k==='escape'){actionsRef.current.escape();return;}
      if(k==='enter'){actionsRef.current.enterKey();return;}
      if(ctrl&&k==='z'){e.preventDefault();e.shiftKey?actionsRef.current.redo():actionsRef.current.undo();}
      else if(ctrl&&k==='y'){e.preventDefault();actionsRef.current.redo();}
      else if(k==='delete'||k==='backspace'){e.preventDefault();actionsRef.current.deleteSelection();}};
    const ku=(e:KeyboardEvent)=>{if(e.key==='Shift')setShiftDown(false);};
    window.addEventListener('keydown',kd);window.addEventListener('keyup',ku);
    return()=>{window.removeEventListener('keydown',kd);window.removeEventListener('keyup',ku);};},[tool,pending,draftPoly,selLink,gcatch]);

  const resizing=useRef(false);
  useEffect(()=>{
    const mv=(e:MouseEvent)=>{if(resizing.current)
      setPanelW(Math.min(640,Math.max(280,window.innerWidth-e.clientX)));};
    const up=()=>{resizing.current=false;document.body.style.userSelect='';};
    window.addEventListener('mousemove',mv);window.addEventListener('mouseup',up);
    return()=>{window.removeEventListener('mousemove',mv);window.removeEventListener('mouseup',up);};},[]);

  const rp=availableReturnPeriods(settings.designType,settings.stormRegion);
  const isPipe=selLink?.section.type==='circular';
  const activeTbl=isPipe?KS_VALUES:MANNING_N;
  const activeConds=selLink?condsFor(activeTbl,selLink.material):[];
  const changeSectionType=(t:string)=>{
    if(!selLink)return;
    const pipe=t==='circular';
    const tbl=pipe?KS_VALUES:MANNING_N;
    const fb=pipe?'Concrete - Precast pipes with O-ring joints':'Concrete-lined channels';
    const mat=(tbl as any)[selLink.material]?selLink.material:(tbl as any)[fb]?fb:Object.keys(tbl)[0];
    const cond=migrateCond(tbl,mat,selLink.condition);
    setLinks(p=>relabel(p.map(l=>l.id!==selLink.id?l:
      {...l,section:{...l.section,type:t as any},material:mat,condition:cond,
       ...(pipe?{ks:(KS_VALUES as any)[mat]?.[cond]??l.ks}:{n:(MANNING_N as any)[mat]?.[cond]??l.n})})));};

  const scaleBarM=useMemo(()=>[1,2,5,10,20,50,100,200,500].find(m=>m*view.scale>=60)??500,[view.scale]);
  const canvasCursor=shiftDown?'crosshair'
    :['manhole','catchpit','outfall','connect','catchment'].includes(tool)?'cell'
    :['calibrate','ruler'].includes(tool)?'crosshair'
    :hoverId?'pointer':'default';

  const linkAnnot=(l:LinkX)=>{
    const parts:string[]=[];
    if(theme.show.labels)parts.push(l.label);
    if(theme.show.size)parts.push(`${l.section.width}mm`);
    const r=results.get(l.id);
    if(r&&!r.error){
      if(theme.show.velocity)parts.push(`${r.Vgov.toFixed(2)}m/s`);
      if(theme.show.flow)parts.push(`${r.accRunoff.toFixed(0)}/${r.Qcap.toFixed(0)}L/min`);
      if(theme.show.usage)parts.push(`${r.usage.toFixed(0)}%`);}
    return parts.join(' · ');};

  const renderKImage=(im:ImgItem)=>{
    if(!im.el||!im.visible)return null;
    const p=worldToBase(im.e,im.n);
    return <KImage key={im.id} image={im.el} x={p.x} y={p.y} scaleX={im.mpp} scaleY={im.mpp}
      opacity={im.opacity} listening={!im.locked&&im.id===selImgId}
      draggable={!im.locked&&im.id===selImgId&&tool==='select'}
      onDragEnd={(e:any)=>{const w=baseToWorld(e.target.x(),e.target.y());
        updImg(im.id,{e:w.e,n:w.n});}}/>;};

  const Th=({children}:any)=><th className="px-2 py-1 border border-slate-700 bg-slate-800 text-left font-medium whitespace-nowrap">{children}</th>;
  const Td=({children,cls}:any)=><td className={`px-2 py-1 border border-slate-800 whitespace-nowrap ${cls||''}`}>{children}</td>;
  const nodeLabel=(id:string|null|undefined,fallback:string)=>id?(nodeById.get(id)?.label??'?'):fallback;
  const linkLabel=(id:string)=>links.find(l=>l.id===id)?.label??'?';

  const totCatchArea=gcatch.reduce((s,g)=>s+g.area,0);
  const failing=links.filter(l=>{const r=results.get(l.id);
    return r&&!r.error&&(r.usage>80||r.Vgov<1.0);});
  const allocTotal=(g:GCatch)=>g.allocs.reduce((s,a)=>s+a.pct,0);

  return (
    <div className="flex flex-col h-screen w-full bg-slate-900 text-slate-200 text-sm">
      {/* ---- ribbon ---- */}
      <div className="flex items-center gap-1 px-3 py-2 bg-slate-950 border-b border-slate-800">
        <button
          onClick={() => (onBack ? onBack() : window.location.assign('/'))}
          title="Return to CivEng Cloud Main Menu"
          className="px-2.5 py-1.5 mr-1 text-xs font-medium rounded border border-slate-700 bg-slate-900 hover:bg-slate-800 hover:border-cyan-500/50 text-slate-200 flex items-center gap-1"
        >
          <span>←</span>
          <span>Main Menu</span>
        </button>
        <div className="w-px h-6 bg-slate-700 mx-1"/>
        <RibbonBtn title="Open" onClick={doOpen}><IconOpen/></RibbonBtn>
        <RibbonBtn title="Save (Ctrl+S)" onClick={()=>doSave(false)}><IconSave/></RibbonBtn>
        <RibbonBtn title="Save As… (Ctrl+Shift+S)" onClick={()=>doSave(true)}><IconSaveAs/></RibbonBtn>
        <RibbonBtn title="Import image / PDF drawing(s)" onClick={()=>imgFileRef.current?.click()}><IconImg/></RibbonBtn>
        <div className="w-px h-6 bg-slate-700 mx-1"/>
        <RibbonBtn title="Undo (Ctrl+Z)" onClick={undo} disabled={!past.length}><IconUndo/></RibbonBtn>
        <RibbonBtn title="Redo (Ctrl+Y)" onClick={redo} disabled={!future.length}><IconRedo/></RibbonBtn>
        <RibbonBtn title="Delete (Del)" onClick={deleteSelection} disabled={!selectedIds.size&&!sel}><IconDel/></RibbonBtn>
        <div className="w-px h-6 bg-slate-700 mx-1"/>
        <RibbonBtn title="Toggle grid" active={gridOn} onClick={()=>setGridOn(g=>!g)}><IconGrid/></RibbonBtn>
        <select value={gridStep} onChange={e=>setGridStep(+e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded px-1 py-1 text-xs">
          <option value={0}>Grid: Auto</option>
          {[0.5,1,2,5,10,20,50].map(s=><option key={s} value={s}>Grid: {s} m</option>)}
        </select>
        <RibbonBtn title="Properties" active={showProps} onClick={()=>setShowProps(p=>!p)}><IconGear/></RibbonBtn>
        <div className="w-px h-6 bg-slate-700 mx-1"/>
        {([['nodes','Nodes'],['links','UC/Pipes'],['catch','Catchments'],['images','Images']] as [keyof Theme['show'],string][]).map(([k,lab])=>(
          <button key={k} title={`Show / hide ${lab.toLowerCase()}`}
            onClick={()=>setTheme(t=>({...t,show:{...t.show,[k]:!t.show[k]}}))}
            className={`px-2 py-1 text-xs rounded border ${theme.show[k]
              ?'bg-blue-600 border-blue-400'
              :'bg-slate-800 border-slate-700 text-slate-500 hover:bg-slate-700'}`}>{lab}</button>))}
        <div className="flex rounded overflow-hidden border border-slate-700 ml-2">
          <button onClick={()=>setTab('editor')}
            className={`px-3 py-1 text-xs ${tab==='editor'?'bg-blue-600':'bg-slate-800 hover:bg-slate-700'}`}>Editor</button>
          <button onClick={()=>setTab('tables')}
            className={`px-3 py-1 text-xs ${tab==='tables'?'bg-blue-600':'bg-slate-800 hover:bg-slate-700'}`}>Tables</button>
        </div>
        <div className="w-px h-6 bg-slate-700 mx-1"/>
        <div className="relative">
          <button onClick={()=>setPdfOpen(o=>!o)} disabled={!printArea}
            title={printArea?'Export PDF of print area':'Use the printarea tool first'}
            className="px-2 py-1 text-xs rounded border border-slate-700 bg-slate-800 hover:bg-slate-700 disabled:opacity-30">PDF</button>
          {pdfOpen&&<div className="absolute top-full mt-1 left-0 z-50 bg-slate-900 border border-slate-700 rounded p-2 space-y-2 w-48 text-xs">
            <select value={pdfOpts.paper} onChange={e=>setPdfOpts(o=>({...o,paper:e.target.value as any}))}
              className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5">
              {['A1','A3','A4'].map(p=><option key={p}>{p}</option>)}</select>
            <label className="flex items-center gap-1"><input type="checkbox" checked={pdfOpts.landscape}
              onChange={e=>setPdfOpts(o=>({...o,landscape:e.target.checked}))}/>Landscape</label>
            <select value={String(pdfOpts.scale)} onChange={e=>setPdfOpts(o=>({...o,scale:e.target.value==='fit'?'fit':+e.target.value}))}
              className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5">
              <option value="fit">Fit to area</option>
              {[100,200,250,500,1000].map(s=><option key={s} value={s}>1:{s}</option>)}</select>
            <button onClick={doPdf} className="w-full rounded bg-blue-600 py-1">Export</button>
          </div>}
        </div>
        <button onClick={doXlsx}
          className="px-2 py-1 text-xs rounded border border-slate-700 bg-slate-800 hover:bg-slate-700">XLSX</button>
        <div className="relative">
          <button onClick={()=>setOptOpen(o=>!o)} disabled={!links.length}
            className="px-2 py-1 text-xs rounded border border-slate-700 bg-slate-800 hover:bg-slate-700 disabled:opacity-30">Optimise</button>
          {optOpen&&<div className="absolute top-full mt-1 right-0 z-50 bg-slate-900 border border-slate-700 rounded p-2 w-72 text-xs space-y-1">
            <div className="grid grid-cols-2 gap-x-2 gap-y-1">
              <NumRow label="Max utilisation (%)" val={optP.maxUsage} onCh={v=>setOptP(p=>({...p,maxUsage:v}))}/>
              <NumRow label="Max depth GL−IL (m)" val={optP.maxDepth} onCh={v=>setOptP(p=>({...p,maxDepth:v}))}/>
              <NumRow label="Min pipe cover (m)" val={optP.minCover} onCh={v=>setOptP(p=>({...p,minCover:v}))}/>
              <NumRow label="Min velocity (m/s)" val={optP.vMin} onCh={v=>setOptP(p=>({...p,vMin:v}))}/>
              <NumRow label="Max velocity (m/s)" val={optP.vMax} onCh={v=>setOptP(p=>({...p,vMax:v}))}/>
              <NumRow label="Min size (mm)" val={optP.minSize} onCh={v=>setOptP(p=>({...p,minSize:v}))}/>
              <NumRow label="Max size (mm)" val={optP.maxSize} onCh={v=>setOptP(p=>({...p,maxSize:v}))}/>
              <NumRow label="Size increment (mm)" val={optP.sizeStep} onCh={v=>setOptP(p=>({...p,sizeStep:v}))}/>
              <NumRow label="Max no. of pipes" val={optP.maxPipes} onCh={v=>setOptP(p=>({...p,maxPipes:v}))}/>
              <NumRow label="Invert step (m)" val={optP.ilStep} onCh={v=>setOptP(p=>({...p,ilStep:v}))}/>
            </div>
            <button onClick={runOptimize} className="w-full rounded bg-blue-600 py-1">Run — sizes &amp; inverts</button>
            <div className="text-slate-600">Upstream→downstream. Smallest size first, deepens before upsizing; pipes go multi-barrel; U-channels cap at max size/depth. One Ctrl+Z undoes everything.</div>
          </div>}
        </div>
        <span className="ml-3 text-xs text-slate-500">
          {fileName?<span className="text-slate-300">{fileName}</span>:'unsaved'}
          {saveMsg&&<span className="text-emerald-400 ml-2">{saveMsg}</span>}
        </span>

        {/* Quick Help Popover Toggle (replaces bottom-right paragraph) */}
        <div className="ml-auto flex items-center gap-2 relative">
          <span className="hidden xl:inline text-xs text-slate-500">Right-click = Select · Enter = finish · Esc = cancel</span>
          <button
            onClick={() => setHelpOpen(h => !h)}
            title="Quick Drawing & Editing Guide"
            className={`px-2 py-1 text-xs rounded border ${
              helpOpen ? 'bg-blue-600 border-blue-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
            }`}
          >
            ? Guide
          </button>
          {helpOpen && (
            <div className="absolute top-full right-0 mt-1.5 w-80 p-3 rounded-lg bg-slate-900 border border-slate-700 shadow-2xl text-xs text-slate-300 space-y-1.5 z-50">
              <div className="font-semibold text-white flex justify-between items-center">
                <span>Quick Workflow Guide</span>
                <button onClick={() => setHelpOpen(false)} className="text-slate-400 hover:text-white">×</button>
              </div>
              <p>• <strong>Connect (UC/Pipe):</strong> Click bend points between nodes for kinked channels; press <kbd className="px-1 bg-slate-800 rounded">Enter</kbd> to finish.</p>
              <p>• <strong>Catchments:</strong> Outline any polygon area, then split its runoff across channels by % in the right panel.</p>
              <p>• <strong>Labels & Vertices:</strong> Drag any label to declutter. Double-click a line to insert a bend vertex.</p>
              <p>• <strong>PDF Underlays:</strong> Import PDFs via the Image button; each page becomes a calibratable layer.</p>
            </div>
          )}
        </div>

        <input ref={fileRef} type="file" accept=".json" onChange={onFile} className="hidden"/>
        <input ref={imgFileRef} type="file" accept="image/*,.pdf,application/pdf" multiple onChange={onImgFile} className="hidden"/>
      </div>

      {/* =============== TABLES TAB =============== */}
      {tab==='tables'&&(
        <div className="flex-1 overflow-auto p-4 space-y-6 text-xs">
          <section>
            <h2 className="text-sm font-semibold mb-1">Pipe / U-Channel Schedule</h2>
            <div className="text-slate-500 mb-2">Design return period {settings.returnPeriod} yr ·
              max 80% utilisation to reserve for sedimentation · V<sub>min</sub> 1.00 m/s (GMS 8.3.4)</div>
            <div className="overflow-auto border border-slate-800 rounded">
            <table className="border-collapse w-full">
              <thead><tr>
                <Th>ID</Th><Th>US CP/MH</Th><Th>DS CP/MH</Th><Th>Type</Th><Th>Size (mm)</Th>
                <Th>Length (m)</Th><Th>US IL (mPD)</Th><Th>DS IL (mPD)</Th><Th>Sf (m/m)</Th><Th>1 in x</Th>
                <Th>Adopted Eq.</Th><Th>V (m/s)</Th><Th>tc (min)</Th><Th>i (mm/hr)</Th>
                <Th>Qreq (L/min)</Th><Th>Qcap (L/min)</Th><Th>Usage</Th><Th>Check</Th>
              </tr></thead>
              <tbody>
              {links.map(l=>{
                const r=results.get(l.id); const len=lengthOf(l);
                const sf=len>0?(l.usInvert-l.dsInvert)/len:0;
                const ok=r&&!r.error;
                const over=ok&&r.usage>80, slow=ok&&r.Vgov<1.0;
                return(<tr key={l.id} className="odd:bg-slate-900 even:bg-slate-950">
                  <Td cls="font-semibold">{l.label}</Td>
                  <Td>{nodeLabel(l.fromNodeId,'-')}</Td>
                  <Td>{nodeLabel(l.toNodeId,'(outfall)')}</Td>
                  <Td>{l.section.type==='circular'?'Pipe':l.section.type==='uchannel'?'U-channel':l.section.type}</Td>
                  <Td>{l.section.width}</Td>
                  <Td>{len.toFixed(1)}</Td>
                  <Td>{'+'+l.usInvert.toFixed(2)}</Td>
                  <Td>{'+'+l.dsInvert.toFixed(2)}</Td>
                  <Td cls={sf<=0?'text-red-400':''}>{sf.toFixed(4)}</Td>
                  <Td>{sf>0?(1/sf).toFixed(1):'—'}</Td>
                  <Td>{ok?r.equation:'—'}</Td>
                  <Td cls={slow?'text-amber-400':''}>{ok?r.Vgov.toFixed(2):'—'}</Td>
                  <Td>{ok?r.tc.toFixed(2):'—'}</Td>
                  <Td>{ok?r.i.toFixed(1):'—'}</Td>
                  <Td>{ok?r.accRunoff.toFixed(0):'—'}</Td>
                  <Td>{ok?r.Qcap.toFixed(0):'—'}</Td>
                  <Td cls={over?'text-red-400 font-semibold':'text-emerald-400'}>
                    {ok?r.usage.toFixed(1)+'%':'—'}</Td>
                  <Td cls={!ok?'text-red-400':over||slow?'text-red-400':'text-emerald-400'}>
                    {!ok?(r?.error??'—'):over?'⚠ >80%':slow?'⚠ V<1.0':'OK'}</Td>
                </tr>);})}
              </tbody>
            </table></div>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-2">Manholes / Catchpits / Outfalls</h2>
            <div className="overflow-auto border border-slate-800 rounded inline-block">
            <table className="border-collapse">
              <thead><tr><Th>Label</Th><Th>Node</Th><Th>Type (DSD Std. Dwg.)</Th><Th>Easting (m)</Th><Th>Northing (m)</Th>
                <Th>GL (mPD)</Th><Th>IL (mPD)</Th><Th>Depth (m)</Th><Th>Connections</Th></tr></thead>
              <tbody>{nodes.map(n=>(
                <tr key={n.id} className="odd:bg-slate-900 even:bg-slate-950">
                  <Td cls="font-semibold">{n.label}</Td><Td>{n.type}</Td>
                  <Td>{n.type==='outfall'?'—':effNodeType(n)}{n.ntype?' *':''}</Td>
                  <Td>{n.e.toFixed(2)}</Td><Td>{n.n.toFixed(2)}</Td>
                  <Td>{'+'+n.groundLevel.toFixed(2)}</Td><Td>{'+'+n.invertLevel.toFixed(2)}</Td>
                  <Td>{(n.groundLevel-n.invertLevel).toFixed(2)}</Td>
                  <Td>{links.filter(l=>l.fromNodeId===n.id||l.toNodeId===n.id).map(l=>l.label).join(', ')||'—'}</Td>
                </tr>))}</tbody>
            </table></div>
            <div className="text-slate-600 mt-1">* manually overridden type</div>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-2">Catchment Schedule</h2>
            <div className="overflow-auto border border-slate-800 rounded inline-block">
            <table className="border-collapse">
              <thead><tr><Th>Catchment</Th><Th>Area (m²)</Th><Th>Type of Surface</Th>
                <Th>C</Th><Th>H (m/100m)</Th><Th>L (m)</Th><Th>Allocated to</Th><Th>Σ%</Th><Th>Source</Th></tr></thead>
              <tbody>{gcatch.map(g=>{const tot=allocTotal(g);
                return(<tr key={g.id} className="odd:bg-slate-900 even:bg-slate-950">
                  <Td cls="font-semibold">{g.name}</Td><Td>{g.area}</Td>
                  <Td>{g.surfaceType}</Td><Td>{g.C}</Td><Td>{g.H}</Td><Td>{g.L}</Td>
                  <Td>{g.allocs.map(a=>`${linkLabel(a.linkId)} ${a.pct}%`).join(', ')||'—'}</Td>
                  <Td cls={tot===100?'text-emerald-400':'text-amber-400'}>{tot}%</Td>
                  <Td>{g.poly?'drawn polygon':'manual'}</Td>
                </tr>);})}</tbody>
            </table></div>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-2">Summary / Checks</h2>
            <div className="space-y-1 text-slate-300">
              <div>Total catchment area: <span className="font-mono">{totCatchArea.toLocaleString()} m²</span></div>
              <div>Components: {nodes.length} nodes · {links.length} channels/pipes · {gcatch.length} catchments</div>
              {gcatch.some(g=>allocTotal(g)!==100)&&
                <div className="text-amber-400">⚠ Catchments not allocated to exactly 100%: {gcatch.filter(g=>allocTotal(g)!==100).map(g=>g.name).join(', ')}</div>}
              {failing.length===0
                ?<div className="text-emerald-400">All sections OK — usage ≤ 80% and V ≥ 1.0 m/s.</div>
                :<div className="text-red-400">⚠ Sections requiring attention: {failing.map(l=>l.label).join(', ')}</div>}
            </div>
          </section>
        </div>)}

      {/* =============== EDITOR TAB =============== */}
      {tab==='editor'&&(
      <div className="flex flex-1 min-h-0">
        {/* ---- Collapsible Left Tool Sidebar ---- */}
        <div className="flex flex-col gap-2 p-2.5 bg-slate-950 border-r border-slate-800 w-52 overflow-auto">
          {TOOL_GROUPS.map(grp => {
            const isOpen = openGroups[grp.id] ?? true;
            return (
              <div key={grp.id} className="border border-slate-800/90 rounded-lg bg-slate-900/40 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenGroups(g => ({ ...g, [grp.id]: !isOpen }))}
                  className="w-full flex items-center justify-between px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200 bg-slate-900/80"
                >
                  <span>{grp.title}</span>
                  <span>{isOpen ? '▾' : '▸'}</span>
                </button>
                {isOpen && (
                  <div className="p-1.5 flex flex-col gap-1">
                    {grp.tools.map(t => (
                      <button
                        key={t}
                        onClick={() => {
                          setTool(t);
                          setPending(null);
                          setDraftPoly([]);
                          setCalPts([]);
                          setRulPts([]);
                          setPaPts([]);
                          setDraftCtr([]);
                        }}
                        disabled={t === 'calibrate' && !selImg?.el}
                        className={`rounded px-2.5 py-1 text-left text-xs border disabled:opacity-30 transition ${
                          tool === t
                            ? 'bg-blue-600 border-blue-400 text-white font-medium'
                            : 'bg-slate-800/80 border-slate-700/80 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        {TOOL_LABELS[t]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex flex-col gap-1.5 pt-1">
            <button onClick={()=>setShowProps(p=>!p)}
              className={`rounded px-2.5 py-1 text-xs text-left border ${showProps?'bg-blue-600 border-blue-400':'bg-slate-800 border-slate-700 hover:bg-slate-700'}`}>⚙ Display Properties</button>
            <button onClick={autoCalcGL} disabled={!glPts.length&&!glCtrs.length}
              title="One-off: interpolates GL for every node from GL points/contours. Never re-runs automatically."
              className="rounded px-2.5 py-1 text-xs text-left bg-amber-900/80 border border-amber-700 text-amber-200 disabled:opacity-40">⛰ Auto-calc GL</button>
            <button onClick={deleteSelection} disabled={!selectedIds.size&&!sel}
              className="rounded px-2.5 py-1 text-xs text-left bg-red-900/80 border border-red-700 text-red-200 disabled:opacity-40">🗑 Delete Selected</button>
          </div>

          {gcatch.length>0&&(
            <div className="mt-2 border-t border-slate-800 pt-2 space-y-1">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Catchments ({gcatch.length})</span>
              {gcatch.map(g=>{const tot=allocTotal(g);
                return <div key={g.id}
                  className={`flex justify-between items-center text-xs rounded px-1.5 py-0.5 cursor-pointer ${sel?.id===g.id?'bg-emerald-900/50 text-emerald-300':'text-slate-400 hover:bg-slate-800'}`}
                  onClick={()=>{setSel({kind:'catch',id:g.id});setSelectedIds(new Set([g.id]));}}>
                  <span className="truncate">{g.name} · {g.area} m²</span>
                  <span className={tot===100?'text-emerald-500':'text-amber-400'}>{tot}%</span>
                </div>;})}
            </div>)}

          {images.length>0&&(
            <div className="mt-2 space-y-1 border-t border-slate-800 pt-2">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Underlays ({images.length})</span>
              {images.map((im,idx)=>(
                <div key={im.id}
                  className={`rounded border px-1.5 py-1 text-xs cursor-pointer ${im.id===selImgId?'border-blue-500 bg-slate-800':'border-slate-800 hover:bg-slate-800'}`}
                  onClick={()=>setSelImgId(im.id)}>
                  <div className="flex items-center gap-1">
                    <button title="visibility" onClick={e=>{e.stopPropagation();updImg(im.id,{visible:!im.visible});}}
                      className={im.visible?'text-slate-200':'text-slate-600'}>👁</button>
                    <span className={`truncate flex-1 ${im.el?'':'text-amber-400 italic'}`}>{im.name}</span>
                    <span className="text-slate-500">{im.above?'▲':'▼'}</span>
                    <button className="text-red-400" onClick={e=>{e.stopPropagation();removeImg(im.id);}}>×</button>
                  </div>
                  {!im.el&&<div className="text-amber-500">re-import "{im.name}" to restore</div>}
                  {im.id===selImgId&&im.el&&(
                    <div className="mt-1 space-y-1" onClick={e=>e.stopPropagation()}>
                      <label className="block text-slate-400">Opacity
                        <input type="range" min={0.1} max={1} step={0.05} value={im.opacity}
                          onChange={e=>updImg(im.id,{opacity:+e.target.value})} className="w-full"/></label>
                      <div className="flex gap-2 flex-wrap">
                        <label className="flex items-center gap-1 text-slate-400">
                          <input type="checkbox" checked={im.locked}
                            onChange={e=>updImg(im.id,{locked:e.target.checked})}/>Lock</label>
                        <label className="flex items-center gap-1 text-slate-400">
                          <input type="checkbox" checked={im.above}
                            onChange={e=>updImg(im.id,{above:e.target.checked})}/>Above network</label>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={()=>moveImg(im.id,-1)} disabled={idx===0}
                          className="px-1 rounded bg-slate-700 disabled:opacity-30">↑</button>
                        <button onClick={()=>moveImg(im.id,1)} disabled={idx===images.length-1}
                          className="px-1 rounded bg-slate-700 disabled:opacity-30">↓</button>
                        <span className="text-slate-500 ml-1">{im.mpp.toFixed(4)} m/px</span>
                      </div>
                      {!im.locked&&<div className="text-pink-400">Drag pink corners to scale · drag image to move</div>}
                    </div>)}
                </div>))}
            </div>)}

          {measures.length>0&&(
            <div className="mt-2 border-t border-slate-800 pt-2 space-y-1">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Measurements</span>
              {measures.map(m=>{const d=Math.hypot(m.b.e-m.a.e,m.b.n-m.a.n);
                return <div key={m.id}
                  className={`flex justify-between text-xs rounded px-1 cursor-pointer ${sel?.id===m.id?'bg-lime-900/50 text-lime-300':'text-slate-400 hover:bg-slate-800'}`}
                  onClick={()=>{setSel({kind:'measure',id:m.id});setSelectedIds(new Set([m.id]));}}>
                  <span>{d.toFixed(2)} m</span>
                  <button className="text-red-400" onClick={e=>{e.stopPropagation();
                    setMeasures(p=>p.filter(x=>x.id!==m.id));
                    if(sel?.id===m.id){setSel(null);setSelectedIds(new Set());}}}>×</button>
                </div>;})}
            </div>)}
          <div className="mt-auto pt-2 text-[11px] text-slate-500 flex justify-between">
            <span>Snap {SNAP} m</span>
            {selectedIds.size>1&&<span className="text-cyan-400">{selectedIds.size} selected</span>}
          </div>
        </div>

        <div ref={wrapRef} className="flex-1 relative" style={{cursor:canvasCursor,background:theme.bg}}
          onContextMenu={e=>{e.preventDefault();
            if(tool==='contour'){
              if(draftCtr.length>=2){finishContour();return;}
              setDraftCtr([]);setTool('select');return;}
            if(tool==='catchment'){
              if(draftPoly.length>=3){commitCatchment(draftPoly);return;}
              setDraftPoly([]);setTool('select');return;}
            setTool('select');
            setPending(null);setDraftPoly([]);setCalPts([]);setRulPts([]);setDraftCtr([]);}}>
          <Stage width={size.w} height={size.h} onClick={onStageClick} onWheel={onWheel}
            onDblClick={()=>{if(tool==='connect')finishConnect();}}
            onMouseDown={onStageMouseDown} onMouseMove={onStageMouseMove} onMouseUp={onStageMouseUp}
            draggable={tool==='select'&&!shiftDown}
            onDragEnd={e=>{const s=e.target;if(s===s.getStage())setView(v=>({...v,x:s.x(),y:s.y()}));}}
            x={view.x} y={view.y} scaleX={view.scale} scaleY={view.scale}>
            <Layer>
              {theme.show.images&&images.filter(i=>!i.above).map(renderKImage)}
              {gridLines}
              {glCtrs.map(c=>{
                const isSel=(sel?.kind==='contour'&&sel.id===c.id)||selectedIds.has(c.id);
                const pts=c.pts.flatMap(p=>{const b=worldToBase(p.e,p.n);return[b.x,b.y];});
                const mb=worldToBase(c.pts[0].e,c.pts[0].n);
                return <Group key={c.id}>
                  <Line points={pts} stroke={isSel?theme.selected:'#b45309'}
                    strokeWidth={isSel?0.55:0.35} dash={[3,1.5]} hitStrokeWidth={4}
                    onClick={(e:any)=>{if(tool!=='select')return;e.cancelBubble=true;
                      setSel({kind:'contour',id:c.id});setSelectedIds(new Set([c.id]));}}
                    onDblClick={(e:any)=>{if(tool!=='select')return;e.cancelBubble=true;
                      const pos=e.target.getStage().getPointerPosition();
                      insertCtrVertex(c,stageToWorld(pos.x,pos.y));}}/>
                  {isSel&&tool==='select'&&c.pts.map((p,vi)=>{const b=worldToBase(p.e,p.n);
                    return <RegularPolygon key={vi} sides={4} radius={1.5} rotation={45}
                      x={b.x} y={b.y} fill="#fff" stroke={theme.selected} strokeWidth={0.3} draggable
                      onDragStart={onDragStartHist}
                      onDragMove={(e:any)=>dragCtrVertex(c.id,vi,e)}
                      onDragEnd={onDragEndHist}
                      onDblClick={(e:any)=>{e.cancelBubble=true;removeCtrVertex(c.id,vi);}}
                      onClick={(e:any)=>{e.cancelBubble=true;}}/>;})}
                  <Text x={mb.x+1} y={mb.y-theme.textSize} text={`+${c.z}`}
                    fontSize={theme.textSize*0.9} fill={isSel?theme.selected:'#d97706'} listening={false}/>
                </Group>;})}
              {glPts.map(g=>{const b=worldToBase(g.e,g.n);
                return <Group key={g.id} x={b.x} y={b.y}
                  draggable={tool==='select'}
                  onDragStart={onDragStartHist}
                  onDragMove={(e:any)=>{if(e.target!==e.currentTarget)return;
                    const w=baseToWorld(e.target.x(),e.target.y());
                    setGlPts(p=>p.map(x=>x.id===g.id?{...x,e:snap(w.e),n:snap(w.n)}:x));}}
                  onDragEnd={onDragEndHist}
                  onDblClick={(e:any)=>{e.cancelBubble=true;setGlPts(p=>p.filter(x=>x.id!==g.id));}}>
                  <RegularPolygon sides={3} radius={1.6} fill="#f59e0b" stroke="#78350f" strokeWidth={0.3}/>
                  <Text x={2} y={-theme.textSize/2} text={`+${g.z}`}
                    fontSize={theme.textSize*0.9} fill="#fbbf24" listening={false}/>
                </Group>;})}
              {draftCtr.length>0&&(()=>{
                const seq=[...draftCtr,...(cursor?[cursor]:[])];
                const pts=seq.flatMap(p=>{const b=worldToBase(p.e,p.n);return[b.x,b.y];});
                return <Line points={pts} stroke="#f59e0b" strokeWidth={0.5} dash={[2,1.5]} listening={false}/>;})()}
              {theme.show.catch&&gcatch.filter(g=>g.poly).map(g=>{
                const pts=g.poly!.flatMap(p=>{const b=worldToBase(p.e,p.n);return[b.x,b.y];});
                const isSel=(sel?.kind==='catch'&&sel.id===g.id)||selectedIds.has(g.id);
                const tot=allocTotal(g); const bad=tot!==100;
                const c=centroid(g.poly!); const cb=worldToBase(c.e,c.n);
                return <Group key={g.id}>
                  <Line points={pts} closed
                    fill={theme.catch+(isSel?'33':'1a')}
                    stroke={isSel?theme.catch:bad?'#fbbf24':theme.catch+'55'} strokeWidth={isSel?0.6:0.4}
                    onClick={(e:any)=>{if(tool!=='select')return;e.cancelBubble=true;
                      setSel({kind:'catch',id:g.id});setSelectedIds(new Set([g.id]));}}
                    onDblClick={(e:any)=>{if(tool!=='select')return;e.cancelBubble=true;
                      const pos=e.target.getStage().getPointerPosition();
                      insertCatchVertex(g,stageToWorld(pos.x,pos.y));}}/>
                  {theme.show.labels&&<Text x={cb.x} y={cb.y} text={`${g.name}${bad?' ⚠'+tot+'%':''}`}
                    fontSize={theme.textSize} fill={bad?'#fbbf24':theme.catch} listening={false}/>}
                  {isSel&&tool==='select'&&g.poly!.map((p,vi)=>{const b=worldToBase(p.e,p.n);
                    return <Circle key={vi} x={b.x} y={b.y} radius={1.4}
                      fill={theme.catch} stroke="#fff" strokeWidth={0.3} draggable
                      onDragStart={onDragStartHist}
                      onDragMove={(e:any)=>dragCatchVertex(g.id,vi,e)}
                      onDragEnd={onDragEndHist}
                      onDblClick={(e:any)=>{e.cancelBubble=true;removeCatchVertex(g.id,vi);}}
                      onClick={(e:any)=>{e.cancelBubble=true;}}/>;})}
                </Group>;})}
              {theme.show.links&&links.map(l=>{
                const path=linkPath(l);if(!path)return null;
                const flat=path.flatMap(p=>{const b=worldToBase(p.e,p.n);return[b.x,b.y];});
                const isSel=(sel?.kind==='link'&&sel.id===l.id)||selectedIds.has(l.id);
                const hov=hoverId===l.id;
                const isPipeL=l.section.type==='circular';
                const base=isPipeL?theme.pipe:theme.channel;
                const col=isSel?theme.selected:hov?'#94a3b8':base;
                const r=results.get(l.id);
                const over=r&&!r.error&&r.usage>80&&theme.show.usage;
                const mid=midOfPath(path); const mb=worldToBase(mid.e,mid.n);
                const off=l.labelOff??{x:0,y:-theme.textSize*0.8};
                const endB=worldToBase(path[path.length-1].e,path[path.length-1].n);
                return(
                  <Group key={l.id}
                    onClick={(e:any)=>{if(['catchment','calibrate','ruler','gl','contour'].includes(tool))return;
                      if(tool==='connect')return;
                      e.cancelBubble=true;setSel({kind:'link',id:l.id});setSelectedIds(new Set([l.id]));}}
                    onDblClick={(e:any)=>{if(tool!=='select')return;e.cancelBubble=true;
                      const pos=e.target.getStage().getPointerPosition();
                      insertLinkVertex(l,stageToWorld(pos.x,pos.y));}}
                    onMouseEnter={()=>setHoverId(l.id)} onMouseLeave={()=>setHoverId(h=>h===l.id?null:h)}>
                    <Arrow points={flat} hitStrokeWidth={5}
                      stroke={col} fill={col}
                      strokeWidth={theme.lineWidth*(isSel?1.5:hov?1.25:1)}
                      pointerLength={3} pointerWidth={3}
                      dash={isPipeL?[theme.lineWidth*2,theme.lineWidth*1.8]:undefined}/>
                    {!l.toNodeId&&<Circle x={endB.x} y={endB.y} radius={2.2} stroke={col} strokeWidth={0.6}/>}
                    {isSel&&tool==='select'&&(l.vertices??[]).map((v,vi)=>{const b=worldToBase(v.e,v.n);
                      return <RegularPolygon key={vi} sides={4} radius={1.5} rotation={45}
                        x={b.x} y={b.y} fill="#fff" stroke={theme.selected} strokeWidth={0.3} draggable
                        onDragStart={onDragStartHist}
                        onDragMove={(e:any)=>dragLinkVertex(l.id,vi,e)}
                        onDragEnd={onDragEndHist}
                        onDblClick={(e:any)=>{e.cancelBubble=true;removeLinkVertex(l.id,vi);}}
                        onClick={(e:any)=>{e.cancelBubble=true;}}/>;})}
                    <Text x={mb.x+off.x} y={mb.y+off.y}
                      text={linkAnnot(l)} fontSize={theme.textSize}
                      fill={over?'#f87171':theme.text}
                      listening={tool==='select'} draggable={tool==='select'}
                      onDragStart={onDragStartHist}
                      onDragMove={(e:any)=>updLink(l.id,{labelOff:{x:e.target.x()-mb.x,y:e.target.y()-mb.y}})}
                      onDragEnd={onDragEndHist}
                      onClick={(e:any)=>{e.cancelBubble=true;setSel({kind:'link',id:l.id});setSelectedIds(new Set([l.id]));}}/>
                  </Group>);})}
              {draftPoly.length>0&&(()=>{
                const seq=[...draftPoly,...(cursor?[cursor]:[])];
                const pts=seq.flatMap(p=>{const b=worldToBase(p.e,p.n);return[b.x,b.y];});
                const first=worldToBase(draftPoly[0].e,draftPoly[0].n);
                const last=worldToBase(seq[seq.length-1].e,seq[seq.length-1].n);
                return <Group listening={false}>
                  <Line points={pts} stroke={theme.catch} strokeWidth={0.5} dash={[2,1.5]}/>
                  {draftPoly.length>=3&&<Line points={[last.x,last.y,first.x,first.y]}
                    stroke="#fbbf24" strokeWidth={0.3} dash={[1,1.5]}/>}
                  <Circle x={first.x} y={first.y} radius={2} stroke="#fbbf24" strokeWidth={0.6} dash={[1.5,1]}/>
                </Group>;})()}
              {pending&&(()=>{
                const start=pending.nodeId?nodeById.get(pending.nodeId)!:pending.point!;
                const seq=[{e:start.e,n:start.n},...pending.verts,...(cursor?[cursor]:[])];
                const pts=seq.flatMap(p=>{const b=worldToBase(p.e,p.n);return[b.x,b.y];});
                const sb=worldToBase(start.e,start.n);
                return <Group listening={false}>
                  <Line points={pts} stroke={theme.selected} strokeWidth={0.5} dash={[2,1.5]}/>
                  <Circle x={sb.x} y={sb.y} radius={2} fill={theme.selected}/>
                  {pending.verts.map((v,i)=>{const b=worldToBase(v.e,v.n);
                    return <Circle key={i} x={b.x} y={b.y} radius={1.2} fill={theme.selected}/>;})}
                </Group>;})()}
              {calPts.map((p,i)=>{const b=worldToBase(p.e,p.n);
                return <Circle key={i} x={b.x} y={b.y} radius={2} stroke="#f472b6" strokeWidth={0.8} listening={false}/>;})}
              {tool==='calibrate'&&calPts.length===1&&cursor&&(()=>{
                const a=worldToBase(calPts[0].e,calPts[0].n),b=worldToBase(cursor.e,cursor.n);
                const d=Math.hypot(cursor.e-calPts[0].e,cursor.n-calPts[0].n);
                return(<Group listening={false}>
                  <Line points={[a.x,a.y,b.x,b.y]} stroke="#f472b6" strokeWidth={0.6} dash={[2,1.5]}/>
                  <Text x={(a.x+b.x)/2+2} y={(a.y+b.y)/2-theme.textSize}
                    text={d.toFixed(2)+' m'} fontSize={theme.textSize} fill="#f472b6"/>
                </Group>);})()}
              {measures.map(m=>{
                const a=worldToBase(m.a.e,m.a.n),b=worldToBase(m.b.e,m.b.n);
                const d=Math.hypot(m.b.e-m.a.e,m.b.n-m.a.n);
                const isSel=sel?.id===m.id||selectedIds.has(m.id);
                const hov=hoverId===m.id;
                return(<Group key={m.id}
                  onClick={(e:any)=>{if(tool!=='select')return;e.cancelBubble=true;
                    setSel({kind:'measure',id:m.id});setSelectedIds(new Set([m.id]));}}
                  onMouseEnter={()=>setHoverId(m.id)} onMouseLeave={()=>setHoverId(h=>h===m.id?null:h)}>
                  <Line points={[a.x,a.y,b.x,b.y]} hitStrokeWidth={4}
                    stroke={isSel?'#fde047':hov?'#d9f99d':theme.measure}
                    strokeWidth={isSel?0.9:0.6} dash={[2,1.5]}/>
                  <Circle x={a.x} y={a.y} radius={0.8} fill={theme.measure} listening={false}/>
                  <Circle x={b.x} y={b.y} radius={0.8} fill={theme.measure} listening={false}/>
                  <Text x={(a.x+b.x)/2+2} y={(a.y+b.y)/2-theme.textSize}
                    text={d.toFixed(2)+' m'} fontSize={theme.textSize}
                    fill={isSel?'#fde047':theme.measure} listening={false}/>
                </Group>);})}
              {tool==='ruler'&&rulPts.length===1&&cursor&&(()=>{
                const a=worldToBase(rulPts[0].e,rulPts[0].n),b=worldToBase(cursor.e,cursor.n);
                const d=Math.hypot(cursor.e-rulPts[0].e,cursor.n-rulPts[0].n);
                return(<Group listening={false}>
                  <Line points={[a.x,a.y,b.x,b.y]} stroke={theme.measure} strokeWidth={0.6} dash={[2,1.5]}/>
                  <Text x={(a.x+b.x)/2+2} y={(a.y+b.y)/2-theme.textSize}
                    text={d.toFixed(2)+' m'} fontSize={theme.textSize} fill={theme.measure}/>
                </Group>);})()}
              {printArea&&(()=>{const a=worldToBase(printArea.e0,printArea.n0),b=worldToBase(printArea.e1,printArea.n1);
                return <Line points={[a.x,a.y,b.x,a.y,b.x,b.y,a.x,b.y]} closed stroke="#f8fafc"
                  strokeWidth={0.5} dash={[3,2]} listening={false}/>;})()}
              {tool==='printarea'&&paPts.length===1&&cursor&&(()=>{
                const a=worldToBase(paPts[0].e,paPts[0].n),b=worldToBase(cursor.e,cursor.n);
                return <Line points={[a.x,a.y,b.x,a.y,b.x,b.y,a.x,b.y]} closed stroke="#f8fafc"
                  strokeWidth={0.4} dash={[2,2]} listening={false}/>;})()}
              {theme.show.nodes&&nodes.map(n=>{const p=worldToBase(n.e,n.n);
                const isSel=(sel?.kind==='node'&&sel.id===n.id)||selectedIds.has(n.id);
                const hov=hoverId===n.id;
                const fill=(theme as any)[n.type];
                const rad=3.5*theme.nodeScale;
                const off=n.labelOff??{x:rad+1,y:-theme.textSize};
                return(
                  <Group key={n.id} x={p.x} y={p.y} draggable={tool==='select'}
                    onClick={(e:any)=>{if(['catchment','calibrate','ruler','gl','contour'].includes(tool))return;
                      e.cancelBubble=true;onNodeClick(n);}}
                    onMouseEnter={()=>setHoverId(n.id)} onMouseLeave={()=>setHoverId(h=>h===n.id?null:h)}
                    onDragStart={onDragStartHist} onDragMove={(e:any)=>dragNode(n,e)} onDragEnd={onDragEndHist}>
                    <Circle radius={rad+3.5} fill="black" opacity={0}/>
                    {n.type==='catchpit'?<RegularPolygon sides={4} radius={rad} rotation={45}
                        fill={fill} stroke={isSel?theme.selected:hov?'#fff':'#0f172a'} strokeWidth={isSel||hov?1:0.4}/>
                     :n.type==='outfall'?<RegularPolygon sides={3} radius={rad*1.15} rotation={180}
                        fill={fill} stroke={isSel?theme.selected:hov?'#fff':'#0f172a'} strokeWidth={isSel||hov?1:0.4}/>
                     :<Circle radius={rad}
                        fill={fill} stroke={isSel?theme.selected:hov?'#fff':'#0f172a'} strokeWidth={isSel||hov?1:0.4}/>}
                    {theme.show.labels&&<Text x={off.x} y={off.y} text={n.label}
                      fontSize={theme.textSize} fill={theme.text}
                      listening={tool==='select'} draggable={tool==='select'}
                      onDragMove={(e:any)=>{if(e.target===e.currentTarget)
                        updNode(n.id,{labelOff:{x:e.target.x(),y:e.target.y()}});}}
                      onClick={(e:any)=>{e.cancelBubble=true;onNodeClick(n);}}/>}
                  </Group>);})}
              {theme.show.images&&images.filter(i=>i.above).map(renderKImage)}
              {theme.show.images&&selImg&&selImg.el&&!selImg.locked&&selImg.visible&&(()=>{
                const el=selImg.el!;
                const Wm=el.width*selImg.mpp,Hm=el.height*selImg.mpp;
                const cs:Pt[]=[{e:selImg.e,n:selImg.n},{e:selImg.e+Wm,n:selImg.n},
                  {e:selImg.e+Wm,n:selImg.n-Hm},{e:selImg.e,n:selImg.n-Hm}];
                return cs.map((c,i)=>{const b=worldToBase(c.e,c.n);
                  return <Circle key={'ic'+i} x={b.x} y={b.y} radius={2.5}
                    fill="#f472b6" stroke="#fff" strokeWidth={0.4} draggable
                    onDragMove={(e:any)=>{
                      const w=baseToWorld(e.target.x(),e.target.y());
                      const id=selImg.id;
                      setImages(ls=>ls.map(im=>{
                        if(im.id!==id||!im.el)return im;
                        const W2=im.el.width*im.mpp,H2=im.el.height*im.mpp;
                        const cc:Pt[]=[{e:im.e,n:im.n},{e:im.e+W2,n:im.n},
                          {e:im.e+W2,n:im.n-H2},{e:im.e,n:im.n-H2}];
                        const a=cc[(i+2)%4];
                        const d0=Math.hypot(cc[i].e-a.e,cc[i].n-a.n);
                        const d1=Math.hypot(w.e-a.e,w.n-a.n);
                        if(d0<1e-6||d1<1e-6)return im;
                        const f=Math.min(20,Math.max(0.05,d1/d0));
                        return {...im,mpp:im.mpp*f,e:a.e+(im.e-a.e)*f,n:a.n+(im.n-a.n)*f};
                      }));}}/>;});})()}
            </Layer>
          </Stage>
          {marquee&&<div style={{position:'absolute',left:marquee.x,top:marquee.y,width:marquee.w,height:marquee.h,
            border:'1px solid #22d3ee',background:'rgba(34,211,238,0.10)',pointerEvents:'none'}}/>}
          <div className="absolute bottom-2 left-2 text-xs bg-slate-900/80 border border-slate-800 rounded px-2 py-1 pointer-events-none font-mono">
            {cursor?<>E {cursor.e.toFixed(2)} · N {cursor.n.toFixed(2)}</>:'—'}
            {tool==='calibrate'&&<span className="text-pink-400"> · calibrating {selImg?.name??''}{calPts.length===1?' — click 2nd point':''}</span>}
            {tool==='connect'&&<span className="text-cyan-400"> · {pending
              ?`${pending.verts.length} bend(s) — click node or press Enter to finish`
              :'click a start node or point'}</span>}
            {tool==='catchment'&&<span className="text-emerald-400"> · {draftPoly.length===0
              ?'click to start outlining the area'
              :`${draftPoly.length} pts — click 1st point / Enter to close`}</span>}
          </div>
          <div className="absolute bottom-2 right-2 pointer-events-none">
            <div style={{width:scaleBarM*view.scale}} className="h-1 bg-slate-300 border border-slate-500"/>
            <div className="text-[10px] text-slate-400 text-center">{scaleBarM} m</div>
          </div>
        </div>

        <div onMouseDown={()=>{resizing.current=true;document.body.style.userSelect='none';}}
          className="w-1.5 cursor-col-resize bg-slate-800 hover:bg-blue-600"/>

        {/* ---- Right Inspector Panel with Collapsible Accordions ---- */}
        <div style={{width:panelW}} className="bg-slate-950 border-l border-slate-800 overflow-auto p-3 space-y-3 shrink-0">
          {showProps&&(
            <section className="border border-blue-900 rounded-lg p-2.5 space-y-2 bg-slate-900/40">
              <div className="flex justify-between items-center">
                <h3 className="text-xs uppercase tracking-wide text-blue-400 font-semibold">Display properties</h3>
                <button onClick={()=>setTheme(DEFAULT_THEME)} className="text-xs text-slate-400 hover:text-white">reset</button>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {([['bg','Canvas background'],['grid','Grid'],['channel','Channel'],['pipe','Pipe'],
                  ['selected','Selection'],['catch','Catchment'],['measure','Measurement'],['text','Text'],
                  ['manhole','Manhole'],['catchpit','Catchpit'],['outfall','Outfall']] as [keyof Theme,string][])
                  .map(([k,lab])=>(
                  <label key={k} className="flex items-center justify-between text-slate-400">{lab}
                    <input type="color" value={theme[k] as string}
                      onChange={e=>setTheme(t=>({...t,[k]:e.target.value}))}
                      className="w-8 h-5 bg-transparent border border-slate-700 rounded"/></label>))}
              </div>
              <SliderNum label="Node size ×" min={0.5} max={3} step={0.1} val={theme.nodeScale}
                onCh={v=>setTheme(t=>({...t,nodeScale:v}))}/>
              <SliderNum label="Line thickness" min={0.2} max={3} step={0.1} val={theme.lineWidth}
                onCh={v=>setTheme(t=>({...t,lineWidth:v}))}/>
              <SliderNum label="Text size" min={2} max={10} step={0.5} val={theme.textSize}
                onCh={v=>setTheme(t=>({...t,textSize:v}))}/>
              <div className="text-xs text-slate-500 uppercase tracking-wide pt-1">Show on canvas</div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {([['labels','Label ID'],['usage','Utilisation %'],['velocity','Velocity'],
                  ['flow','Qreq / Qcap'],['size','Section size']] as [keyof Theme['show'],string][]).map(([k,lab])=>(
                  <label key={k} className="flex items-center gap-1 text-slate-400">
                    <input type="checkbox" checked={theme.show[k]}
                      onChange={e=>setTheme(t=>({...t,show:{...t.show,[k]:e.target.checked}}))}/>{lab}</label>))}
              </div>
            </section>)}

          {/* Collapsible Project Info */}
          <section className="border border-slate-800 rounded-lg overflow-hidden bg-slate-900/30">
            <button
              type="button"
              onClick={() => setProjOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs uppercase tracking-wide text-slate-400 hover:text-slate-200 bg-slate-900/70 font-semibold"
            >
              <span>Project Info (→ Excel Info tab)</span>
              <span>{projOpen ? '▾' : '▸'}</span>
            </button>
            {projOpen && (
              <div className="p-2.5 space-y-1.5">
                {([['job','Job'],['jobNo','Job No.'],['by','By'],['subject','Subject']] as const).map(([k,lab])=>(
                  <label key={k} className="block text-xs text-slate-400">{lab}
                    <input value={(proj as any)[k]} onChange={e=>setProj(p=>({...p,[k]:e.target.value}))}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-200"/></label>))}
              </div>
            )}
          </section>

          {/* Collapsible Design Settings */}
          <section className="border border-slate-800 rounded-lg overflow-hidden bg-slate-900/30">
            <button
              type="button"
              onClick={() => setDesignOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs uppercase tracking-wide text-slate-400 hover:text-slate-200 bg-slate-900/70 font-semibold"
            >
              <span>Design Settings ({settings.returnPeriod}yr · {settings.designType === 'slope' ? 'TGN30' : 'SDM'})</span>
              <span>{designOpen ? '▾' : '▸'}</span>
            </button>
            {designOpen && (
              <div className="p-2.5 space-y-1.5">
                <label className="block text-xs text-slate-400">Design type</label>
                <select value={settings.designType} onChange={e=>setSettings(s=>({...s,designType:e.target.value as any}))}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1">
                  <option value="slope">Slope crest / toe (TGN30 Table 1)</option>
                  <option value="siteformation">Site formation (SDM Table 3)</option>
                </select>
                {settings.designType==='siteformation'&&(<>
                  <label className="block text-xs text-slate-400">Storm region</label>
                  <select value={settings.stormRegion} onChange={e=>setSettings(s=>({...s,stormRegion:e.target.value as any}))}
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1">
                    <option value="HKO">HKO Headquarters (3a)</option><option value="TaiMoShan">Tai Mo Shan (3b)</option>
                    <option value="WestLantau">West Lantau (3c)</option><option value="NorthDistrict">North District (3d)</option>
                    <option value="DrySeasonWhole">Dry Season Nov–Mar (Table 3) — sensitivity</option>
                    <option value="DrySeasonCore">Core Dry Season Dec–Feb (Table 4) — sensitivity</option>
                  </select>
                  {resolvedStormTable(settings.designType,settings.stormRegion)!==settings.stormRegion&&
                    <div className="text-xs text-amber-400">⚠ sdmTables has no "{settings.stormRegion}" table — HKO (Table 3a) constants are in use.</div>}</>)}
                <label className="block text-xs text-slate-400">Return period (yr)</label>
                <select value={settings.returnPeriod} onChange={e=>setSettings(s=>({...s,returnPeriod:+e.target.value as any}))}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1">
                  {rp.map(r=><option key={r} value={r}>{r}</option>)}
                </select>
                <label className="block text-xs text-slate-400">Climate change period
                  <span className="text-slate-600"> · {settings.designType==='slope'?'TGN30 +28.1%':'SDM +11.1%/+16%'}</span></label>
                <select value={settings.climatePeriod} onChange={e=>setSettings(s=>({...s,climatePeriod:e.target.value as any}))}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1">
                  <option value="before2041">Before 2041 (0%)</option>
                  <option value="mid21">Mid 21st (2041-2060)</option>
                  <option value="end21">End 21st (2081-2100)</option>
                </select>
              </div>
            )}
          </section>

          {selMeasure&&(()=>{const d=Math.hypot(selMeasure.b.e-selMeasure.a.e,selMeasure.b.n-selMeasure.a.n);
            return <section className="border border-slate-800 rounded-lg p-2.5 bg-slate-900/30">
              <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Measurement</h3>
              <div className="text-lg font-mono text-lime-300">{d.toFixed(3)} m</div>
              <div className="text-xs text-slate-500">A: E {selMeasure.a.e.toFixed(2)} N {selMeasure.a.n.toFixed(2)}</div>
              <div className="text-xs text-slate-500">B: E {selMeasure.b.e.toFixed(2)} N {selMeasure.b.n.toFixed(2)}</div>
              <button onClick={deleteSelection} className="mt-2 text-xs rounded px-2 py-1 bg-red-900 border border-red-700">Delete measurement</button>
            </section>;})()}

          {selNode&&(()=>{
            const opts=selNode.type==='catchpit'?CP_TYPES:selNode.type==='manhole'?MH_TYPES:[];
            const eff=effNodeType(selNode);
            const isCustom=!!selNode.ntype&&!opts.includes(selNode.ntype);
            return <section className="border border-slate-800 rounded-lg p-2.5 space-y-1.5 bg-slate-900/30">
              <h3 className="text-xs uppercase tracking-wide text-cyan-400 font-semibold">Node ({selNode.type})</h3>
              <label className="block text-xs text-slate-400">Label
                <input value={selNode.label} onChange={e=>updNode(selNode.id,{label:e.target.value})}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-200"/></label>
              {opts.length>0&&<>
                <label className="block text-xs text-slate-400">Type (DSD Standard Drawings)</label>
                <select value={isCustom?'__custom':(selNode.ntype??'auto')}
                  onChange={e=>{const v=e.target.value;
                    updNode(selNode.id,{ntype:v==='auto'?undefined:v==='__custom'?eff:v});}}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1">
                  <option value="auto">Auto — {autoNodeType(selNode)}</option>
                  {opts.map(o=><option key={o} value={o}>{o}</option>)}
                  <option value="__custom">Custom…</option>
                </select>
                {isCustom&&<input value={selNode.ntype} placeholder="Custom type"
                  onChange={e=>updNode(selNode.id,{ntype:e.target.value})}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-200"/>}
                <div className="text-xs text-slate-500">Depth GL−IL: {(selNode.groundLevel-selNode.invertLevel).toFixed(2)} m
                  {selNode.type==='manhole'&&' (D1 ≤1.5 · E1 ≤3 · F1 >3)'}</div>
              </>}
              <NumRow label="Easting (m)" val={selNode.e} onCh={v=>updNode(selNode.id,{e:v})}/>
              <NumRow label="Northing (m)" val={selNode.n} onCh={v=>updNode(selNode.id,{n:v})}/>
              <NumRow label="Ground level (mPD) — synced to connected channels" val={selNode.groundLevel}
                onCh={v=>setNodeLevel(selNode.id,{groundLevel:v})}/>
              <NumRow label="Invert level (mPD) — synced to connected channels" val={selNode.invertLevel}
                onCh={v=>setNodeLevel(selNode.id,{invertLevel:v})}/>
            </section>;})()}

          {selLink&&(
            <section className="border border-slate-800 rounded-lg p-2.5 space-y-2 bg-slate-900/30">
              <h3 className="text-xs uppercase tracking-wide text-cyan-400 font-semibold">
                {isPipe?'Pipe':'Channel'} {selLink.label} {selLink.fromNodeId?nodeById.get(selLink.fromNodeId)?.label:'(open)'} → {selLink.toNodeId?nodeById.get(selLink.toNodeId)?.label:'(free outfall)'}</h3>
              <label className="block text-xs text-slate-400">Label (rename freely)
                <input value={selLink.label} onChange={e=>updLink(selLink.id,{label:e.target.value})}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-200"/></label>
              {(selLink.vertices??[]).length>0&&<div className="text-xs text-slate-500">
                {selLink.vertices!.length} bend point(s) — drag squares to adjust · dbl-click line to add · dbl-click a square to remove</div>}
              <label className="block text-xs text-slate-400">Section type
                <span className="text-slate-500"> · {isPipe?'pipe → Colebrook ks (Table 14)':'open channel → Manning n (Table 13)'}</span></label>
              <select value={selLink.section.type} onChange={e=>changeSectionType(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1">
                <option value="uchannel">U-channel</option><option value="rectangular">Rectangular</option>
                <option value="trapezoidal">Trapezoidal</option><option value="circular">Circular pipe</option>
              </select>
              <div className="grid grid-cols-2 gap-2">
                <NumRow label={isPipe?'Diameter Ø (mm)':'Width (mm)'} val={selLink.section.width}
                  onCh={v=>updLink(selLink.id,{section:{...selLink.section,width:v}})}/>
                {selLink.section.type==='trapezoidal'&&
                  <NumRow label="Side slope z (H:V)" val={selLink.section.sideSlope??0}
                    onCh={v=>updLink(selLink.id,{section:{...selLink.section,sideSlope:v}})}/>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumRow label={`US GL (mPD)${selLink.fromNodeId?' ↔ '+nodeById.get(selLink.fromNodeId)?.label:''}`}
                  val={usGLOf(selLink)??0}
                  onCh={v=>selLink.fromNodeId?setNodeLevel(selLink.fromNodeId,{groundLevel:v})
                    :updLink(selLink.id,{usGroundLevel:v})}/>
                <NumRow label={`DS GL (mPD)${selLink.toNodeId?' ↔ '+nodeById.get(selLink.toNodeId)?.label:''}`}
                  val={dsGLOf(selLink)??0}
                  onCh={v=>selLink.toNodeId?setNodeLevel(selLink.toNodeId,{groundLevel:v})
                    :updLink(selLink.id,{dsGroundLevel:v})}/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumRow label={`US invert (mPD)${selLink.fromNodeId?' ↔ '+nodeById.get(selLink.fromNodeId)?.label:''}`}
                  val={selLink.usInvert}
                  onCh={v=>selLink.fromNodeId?setNodeLevel(selLink.fromNodeId,{invertLevel:v})
                    :updLink(selLink.id,{usInvert:v})}/>
                <NumRow label={`DS invert (mPD)${selLink.toNodeId?' ↔ '+nodeById.get(selLink.toNodeId)?.label:''}`}
                  val={selLink.dsInvert}
                  onCh={v=>selLink.toNodeId?setNodeLevel(selLink.toNodeId,{invertLevel:v})
                    :updLink(selLink.id,{dsInvert:v})}/>
              </div>
              <div className="text-xs text-slate-500">
                Depth (derived GL−invert): {(()=>{const r=results.get(selLink.id);
                  return r&&!r.error?r.depth.toFixed(2)+' m (avg US/DS)':'—';})()}
                {' '}· Length: {lengthOf(selLink).toFixed(1)} m{(selLink.vertices??[]).length>0&&' (along bends)'}
                {selLink.usInvert<=selLink.dsInvert&&<span className="text-red-400"> · ⚠ zero/adverse gradient</span>}
              </div>

              <label className="block text-xs text-slate-400">
                Material / condition ({isPipe?'Colebrook-White ks, Table 14':'Manning n, Table 13'})</label>
              <select value={selLink.material}
                onChange={e=>{const m=e.target.value;const c=migrateCond(activeTbl,m,selLink.condition);
                  updLink(selLink.id,isPipe
                    ?{material:m,condition:c,ks:(KS_VALUES as any)[m]?.[c]??selLink.ks}
                    :{material:m,condition:c,n:(MANNING_N as any)[m]?.[c]??selLink.n});}}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1">
                {Object.keys(activeTbl).map(m=><option key={m} value={m}>{m}</option>)}
              </select>
              <select value={selLink.condition}
                onChange={e=>{const c=e.target.value as Condition;
                  updLink(selLink.id,isPipe
                    ?{condition:c,ks:(KS_VALUES as any)[selLink.material]?.[c]??selLink.ks}
                    :{condition:c,n:(MANNING_N as any)[selLink.material]?.[c]??selLink.n});}}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1">
                {activeConds.map(c=><option key={c} value={c}>
                  {c} ({isPipe?`ks ${(KS_VALUES as any)[selLink.material]?.[c]} mm`:`n ${(MANNING_N as any)[selLink.material]?.[c]}`})</option>)}
              </select>
              {isPipe
                ?<NumRow label={`ks = ${selLink.ks} mm (override)`} val={selLink.ks} onCh={v=>updLink(selLink.id,{ks:v})}/>
                :<NumRow label={`Manning n = ${selLink.n} (override)`} val={selLink.n} onCh={v=>updLink(selLink.id,{n:v})}/>}

              <div className="flex items-center justify-between mt-2">
                <span className="text-xs uppercase tracking-wide text-slate-500">Catchments feeding {selLink.label}</span>
                <button onClick={()=>{setTool('catchment');setDraftPoly([]);}}
                  className="text-xs bg-emerald-900 border border-emerald-700 rounded px-2 py-0.5">✎ draw new</button>
              </div>
              {gcatch.filter(g=>g.allocs.some(a=>a.linkId===selLink.id&&a.pct>0)).map(g=>{
                const a=g.allocs.find(x=>x.linkId===selLink.id)!;
                return <div key={g.id} onClick={()=>{setSel({kind:'catch',id:g.id});setSelectedIds(new Set([g.id]));}}
                  className="flex justify-between text-xs border border-slate-800 rounded px-2 py-1 cursor-pointer hover:bg-slate-800">
                  <span>{g.name} · {g.area} m² × {a.pct}%</span>
                  <span className="font-mono">{Math.round(g.area*a.pct/100)} m²</span>
                </div>;})}
              {gcatch.every(g=>!g.allocs.some(a=>a.linkId===selLink.id&&a.pct>0))&&
                <div className="text-xs text-slate-600">none — draw one, or allocate an existing catchment to this channel</div>}

              {(()=>{const r=results.get(selLink.id);if(!r)return null;
                if(r.error)return <div className="text-red-400 text-xs">Error: {r.error}</div>;
                const warn=r.usage>80;
                return(
                  <div className="mt-2 rounded bg-slate-900 border border-slate-800 p-2 text-xs space-y-1">
                    <div className="uppercase tracking-wide text-slate-500">Results (live)</div>
                    <Res k={`Velocity (${r.equation})`} v={`${r.Vgov.toFixed(2)} m/s`}/>
                    <Res k="  Manning check" v={`${r.Vman.toFixed(2)} m/s`}/>
                    <Res k="  Colebrook check" v={`${r.Vcw.toFixed(2)} m/s`}/>
                    <Res k="Capacity Qcap" v={`${r.Qcap.toFixed(0)} L/min`}/>
                    <Res k="Time of conc. tc" v={`${r.tc.toFixed(2)} min`}/>
                    <Res k="Rainfall i" v={`${r.i.toFixed(1)} mm/hr`}/>
                    <Res k="Accum. catchment" v={`${r.accArea.toFixed(0)} m²`}/>
                    <Res k="Accum. runoff Qreq" v={`${r.accRunoff.toFixed(0)} L/min`}/>
                    <div className={`flex justify-between font-semibold ${warn?'text-red-400':'text-emerald-400'}`}>
                      <span>Utilisation</span><span>{r.usage.toFixed(1)}% {warn?'⚠ >80%':'OK'}</span></div>
                  </div>);})()}
            </section>)}

          {selCatch&&(()=>{const tot=allocTotal(selCatch);
            return <section className="border border-slate-800 rounded-lg p-2.5 space-y-2 bg-slate-900/30">
              <h3 className="text-xs uppercase tracking-wide text-emerald-400 font-semibold">Catchment {selCatch.name}</h3>
              <div className="grid grid-cols-2 gap-2 items-end">
                <label className="block text-xs text-slate-400">Name
                  <input value={selCatch.name} onChange={e=>updCatch(selCatch.id,{name:e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-200"/></label>
                <NumRow label={`Area (m²)${selCatch.poly?' · from polygon':''}`} val={selCatch.area}
                  onCh={v=>updCatch(selCatch.id,{area:v})}/>
              </div>
              <input list="surfList" value={selCatch.surfaceType}
                onChange={e=>{const t=e.target.value;
                  updCatch(selCatch.id,{surfaceType:t,...(RUNOFF_COEFF[t]!=null?{C:RUNOFF_COEFF[t]}:{})});}}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1"
                placeholder="Surface material (type or pick)"/>
              <div className="grid grid-cols-3 gap-1">
                <NumRow label="C" val={selCatch.C} onCh={v=>updCatch(selCatch.id,{C:v})}/>
                <NumRow label="H (m/100m)" val={selCatch.H} onCh={v=>updCatch(selCatch.id,{H:v})}/>
                <NumRow label="L (m)" val={selCatch.L} onCh={v=>updCatch(selCatch.id,{L:v})}/>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-slate-500">Allocation to channels</span>
                <span className={`text-xs font-mono ${tot===100?'text-emerald-400':'text-amber-400'}`}>Σ {tot}%</span>
              </div>
              {selCatch.allocs.map((a,i)=>(
                <div key={i} className="flex gap-1 items-center">
                  <select value={a.linkId}
                    onChange={e=>updCatch(selCatch.id,{allocs:selCatch.allocs.map((x,j)=>j===i?{...x,linkId:e.target.value}:x)})}
                    className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs">
                    {links.map(l=><option key={l.id} value={l.id}>{l.label}</option>)}
                  </select>
                  <input type="number" min={0} max={100} value={a.pct}
                    onChange={e=>updCatch(selCatch.id,{allocs:selCatch.allocs.map((x,j)=>j===i?{...x,pct:Math.max(0,Math.min(100,parseFloat(e.target.value)||0))}:x)})}
                    className="w-16 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-right"/>
                  <span className="text-xs text-slate-500">%</span>
                  <span className="text-xs text-slate-600 font-mono w-16 text-right">{Math.round(selCatch.area*a.pct/100)} m²</span>
                  <button className="text-red-400 text-xs"
                    onClick={()=>updCatch(selCatch.id,{allocs:selCatch.allocs.filter((_,j)=>j!==i)})}>×</button>
                </div>))}
              {links.length>0&&
                <button onClick={()=>{
                    const used=new Set(selCatch.allocs.map(a=>a.linkId));
                    const next=links.find(l=>!used.has(l.id))??links[0];
                    updCatch(selCatch.id,{allocs:[...selCatch.allocs,
                      {linkId:next.id,pct:Math.max(0,100-tot)}]});}}
                  className="text-xs bg-slate-800 border border-slate-700 rounded px-2 py-0.5">+ allocate to channel</button>}
              {tot!==100&&<div className="text-xs text-amber-400">⚠ Allocations total {tot}% — runoff will be computed on {tot}% of the area.</div>}
              <button onClick={deleteSelection}
                className="text-xs rounded px-2 py-1 bg-red-900 border border-red-700">Delete catchment</button>
            </section>;})()}
            {selCtr&&<section className="border border-slate-800 rounded-lg p-2.5 space-y-2 bg-slate-900/30">
              <h3 className="text-xs uppercase tracking-wide text-amber-500">Ground contour +{selCtr.z}</h3>
              <NumRow label="Level (mPD)" val={selCtr.z}
                onCh={v=>setGlCtrs(p=>p.map(c=>c.id===selCtr.id?{...c,z:v}:c))}/>
              <div className="text-xs text-slate-500">{selCtr.pts.length} vertices — drag squares ·
                dbl-click line to add · dbl-click a square to remove</div>
              <button onClick={deleteSelection}
                className="text-xs rounded px-2 py-1 bg-red-900 border border-red-700">Delete contour</button>
            </section>}
          <datalist id="surfList">{Object.keys(RUNOFF_COEFF).map(t=><option key={t} value={t}/>)}</datalist>

          {!selNode&&!selLink&&!selMeasure&&!selCatch&&!selCtr&&!showProps&&(
            <div className="text-slate-500 text-xs border border-dashed border-slate-800 rounded-lg p-3 text-center">
              Select a node, channel, or catchment on the canvas to inspect or edit its parameters. Need tips? Click <strong>? Guide</strong> in the top-right bar.
            </div>
          )}
        </div>
      </div>)}
    </div>);
}

const NumRow=({label,val,onCh}:{label:string;val:number;onCh:(v:number)=>void})=>(
  <label className="block text-xs text-slate-400">{label}
    <input type="number" value={val} onChange={e=>onCh(parseFloat(e.target.value)||0)}
      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-200"/></label>);
const SliderNum=({label,min,max,step,val,onCh}:{label:string;min:number;max:number;step:number;val:number;onCh:(v:number)=>void})=>(
  <label className="block text-xs text-slate-400">{label} {val}
    <div className="flex items-center gap-2">
      <input type="range" min={min} max={max} step={step} value={val}
        onChange={e=>onCh(+e.target.value)} className="flex-1"/>
      <input type="number" min={min} max={max} step={step} value={val}
        onChange={e=>onCh(Math.max(min,Math.min(max,parseFloat(e.target.value)||min)))}
        className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-right text-slate-200"/>
    </div></label>);
const Res=({k,v}:{k:string;v:string})=>(
  <div className="flex justify-between"><span className="text-slate-400">{k}</span><span>{v}</span></div>);